/**
 * OneGrid — 프레임워크 독립 그리드 뷰
 *
 * 성능: 행·열 가상화 + DOM 풀링, 바뀐 행만 다시 그림, 정렬·필터는 Dataset 엔진(기수정렬·사전 마스크)
 * 편집: 항상 포커스된 숨은 textarea 하나가 곧 편집기 → 한글 IME 첫 글자가 빠지지 않는다
 * 레이아웃: 좌측 고정 영역 + 스크롤 영역, 2단 헤더(그룹), 필터 행, 합계 행, 그룹 머리행
 *
 * ── 파일 나눔 (14차) ──
 * 이 파일: OneGrid 클래스 본체 — 상태(필드 전부, 선언 순서 그대로), 생성자·핵심 공개 API·destroy, 데이터 → 뷰(onData·rebuild·postDisp),
 *          레이아웃 계산·DOM 구성(buildDom), 본문 그리기(render·fillRow·fillCell·고정 상자), 값·표시(displayText·editorOf).
 * 나머지 메서드는 기능별 묶음 클래스에 있고, 파일 끝의 applyMixins 가 OneGrid.prototype 에 붙인다 (믹스인).
 *   grid-filter    필터 API·패널·머리글 필터 메뉴·필터 빌더      grid-find      찾기 패널·자연어 질의·찾아 바꾸기·증분 검색
 *   grid-layout    정렬 API·레이아웃 저장·복원·컬럼/행 상태        grid-group     그룹·트리·서버 그룹·그룹 패널·디테일·페이지
 *   grid-header    머리글·합계 줄·머리글 체크·머리글 마우스        grid-select    포커스·선택·선택 요약·보호·낭독
 *   grid-rows      행 추가·삭제·검증·서버 오류·충돌·확인 창       grid-undo      실행 취소·기록 창·셀 메모·마스킹 해제
 *   grid-export    인쇄·엑셀·PDF·차트·카드 보기/사이드바 API       grid-newrow    새 항목 행
 *   grid-editform  편집 폼                                          grid-edit      셀 편집·한글 IME·사용자 편집기
 *   grid-dropdown  콤보 목록·자동 완성·스핀·달력·툴팁               grid-keys      키보드
 *   grid-mouse     마우스·터치                                      grid-menu      우클릭 메뉴·컬럼 선택기
 *   grid-clipboard 클립보드·채우기 핸들                             grid-rowdrag   행 끌어 놓기·행 이동
 *   grid-input     검증 수준·바이트 제한·쓰기 전 이벤트·삭제 행 표시·일괄 입력 (15차)
 *   grid-rowedit   행 단위 편집 (15차)                              grid-checkcombo 체크 콤보 (15차)
 *   grid-types     공개 타입(GridColumn·GridOptions…)               grid-util      순수 도우미·상수·applyMixins
 * 규칙
 *  - 묶음 클래스(GridFind 등)의 메서드는 첫 매개변수 `this: OneGrid` 로 쓰고 본문은 클래스 안과 같다. 상태 필드는 모두 이 파일 클래스에 둔다
 *    (묶음 클래스에는 필드를 두지 않음 — 필드 초기화 순서를 바꾸지 않기 위해).
 *  - 타입은 아래 `interface OneGrid extends GridFind, …` 로 합쳐지므로 this.xxx() 호출은 어느 파일에서나 그대로.
 *  - 예전 private 멤버는 JSDoc internal 태그 (tsconfig stripInternal 로 d.ts 에서 빠짐). 공개 메서드 이름·매개변수는 그대로.
 *  - 새 메서드는 기능이 맞는 묶음 파일에 추가 (같은 이름을 두 곳에 두면 applyMixins 가 읽을 때 오류).
 *  - 매 프레임 경로(render·fillRow·fillCell·스크롤)는 이 파일에 둔다.
 */
import { Dataset, RowState, StrColumn, toNumber } from './dataset.js';
import { sortView, filterView, quickSearch, buildGroupsMulti, withGroupFooters, pageRange, parseFilterText } from './query.js';
import { formatNumber, displayDate, displayDateTime, maskPrivacy } from './format.js';
import { injectCss, setHtml, setCspMode } from './css.js';
import { ServerRowModel } from './server.js';
import { formulaColumn } from './formula.js';
import { evalRules, cellHtml } from './cellfx.js';
import { SideBar } from './sidebar.js';
import { makeT } from './i18n.js';
import { applyMask, parseTime } from './editors.js';
import { applyPreset, getPreset } from './presets.js';
import { compileFilter, parseFind } from './filters.js';
import { EXTRA, WRAP_LINE_PX, NIR, GROUP_LABEL_MIN_PANE, CHECKISH, fitWidths, collapsedHidden, headerChains, columnAccessOf, placeRowLayout, MAX_SCROLL_PX, applyRowDeltas, COL_TYPES, autoColumns, resolveColumnTypes, isCustomSummary, colAggs, applyMixins } from './grid-util.js';
import { GridFilter } from './grid-filter.js';
import { GridFind } from './grid-find.js';
import { GridLayoutState } from './grid-layout.js';
import { GridGroup } from './grid-group.js';
import { GridHeader } from './grid-header.js';
import { GridSelect } from './grid-select.js';
import { GridRows } from './grid-rows.js';
import { GridUndo } from './grid-undo.js';
import { GridExport } from './grid-export.js';
import { GridNewRow } from './grid-newrow.js';
import { GridEditForm } from './grid-editform.js';
import { GridEdit } from './grid-edit.js';
import { GridDropdown } from './grid-dropdown.js';
import { GridKeys } from './grid-keys.js';
import { GridMouse } from './grid-mouse.js';
import { GridMenu } from './grid-menu.js';
import { GridClipboard } from './grid-clipboard.js';
import { GridRowDrag } from './grid-rowdrag.js';
import { GridInput } from './grid-input.js';
import { GridRowEdit } from './grid-rowedit.js';
import { GridCheckCombo } from './grid-checkcombo.js';
import { formatMulti } from './input.js';
export { isUntouchedRow, fitWidths, stepNumber, replaceText, cycleCheck3, collapsedHidden, headerChains, sanitizeLayout, summarizeValues, columnAccessOf, placeRowLayout, sanitizeFilters, resolveRowState, reorderRows, mergeViewOrder, LAYOUT_COLUMN_KEYS, LAYOUT_OPTION_KEYS, download, applyRowDeltas, mlGroupSegments, headerGroupSegments, KEY_ACTIONS, normalizeKeyCombo, keyComboOf, autoColumns, resolveColumnTypes, isCustomSummary, AGG_LABEL, colAggs, footerLines } from './grid-util.js';
export class OneGrid {
    constructor(host, options) {
        /** 화면 글자 번역 (locale·messages) */
        this.t = makeT();
        /** @internal */ this.cols = [];
        /** @internal */ this.paneWidth = [0, 0, 0];
        /** @internal */ this.headRows = 1;
        // 뷰
        this.view = new Uint32Array(0);
        /** @internal */ this.disp = new Int32Array(0);
        /** 후처리(디테일·그룹 푸터·페이지) 전 disp, 화면 행 번호, 추가 슬롯 표 (disp 값 <= EXTRA) */
        /** @internal */ this.baseDisp = new Int32Array(0);
        /** @internal */ this.rowNo = null;
        /** @internal */ this.extras = [];
        /** 밖에서 정한 보기 순서 (Nexacro 호환 Dataset 의 논리 행): 0 이상 = 물리 행, 음수 = 소계 -(k+1) */
        /** @internal */ this.extOrder = null;
        /** @internal */ this.detailOpen = new Set();
        /** @internal */ this.detailHosts = new Map();
        /** 디테일 높이 (물리 행 → px): setDetailHeight·autoHeight 로 잰 값. 없으면 detail.height */
        /** @internal */ this.detailHts = new Map();
        /** @internal */ this.page = 0;
        /** @internal */ this.pageCount = 1;
        /** @internal */ this.pageTotal = 0;
        /** @internal */ this.groups = [];
        /** @internal */ this.sortKeys = [];
        /** @internal */ this.filterText = new Map();
        /** @internal */ this.extFilters = [];
        /** @internal */ this.quickText = '';
        /** @internal */ this.menuFilters = new Map();
        /** @internal */ this.menuEl = null;
        /** @internal */ this.advFilter = null;
        /** @internal */ this.filterEnabled = true;
        /** @internal */ this.mru = [];
        /** @internal */ this.colMru = new Map();
        /** @internal */ this.lastFilterText = '';
        /** @internal */ this.findEl = null;
        /** @internal */ this.findText = '';
        /** @internal */ this.findMode = 'search';
        /** @internal */ this.findMatches = [];
        /** @internal */ this.findIdx = -1;
        /** @internal */ this.hits = new Set();
        /** @internal */ this.incText = '';
        /** @internal */ this.incTimer = 0;
        /** @internal */ this.builderEl = null;
        /** @internal */ this.popEl = null;
        /** @internal */ this.popClose = null;
        /** @internal */ this.suppressHeaderClick = false;
        /** @internal */ this.initialLayout = null;
        /** @internal */ this.menuClose = null;
        /** @internal */ this.groupCollapseAll = false;
        /** @internal */ this.groupToggled = new Set();
        /** @internal */ this.groupDir = new Map();
        // 트리
        /** @internal */ this.treeLevel = new Map();
        /** @internal */ this.treeKids = new Set();
        /** @internal */ this.treeCollapsed = new Set();
        /** @internal */ this.treeInit = false;
        /** @internal */ this.drag = null;
        // 상태
        /** @internal */ this.focus = { vi: 0, ci: 0 };
        /** @internal */ this.anchor = { vi: 0, ci: 0 };
        /** @internal */ this.checked = new Set();
        /** @internal */ this.errors = new Map();
        // 비동기 검증: 키 row:col → 진행 중(토큰·타이머·중단), 결과 오류, 마지막으로 검사한 값
        /** @internal */ this.asyncPend = new Map();
        /** @internal */ this.asyncErr = new Map();
        /** @internal */ this.asyncVal = new Map();
        /** @internal */ this.asyncSeq = 0;
        /** @internal */ this.asyncWaiters = [];
        /** 마스킹 해제된 셀 (row:col → 타이머) */
        /** @internal */ this.groupArgs = null;
        /** @internal */ this.srm = null;
        /** @internal */ this.liveTimer = null;
        /** @internal */ this.srvDistinct = new Map();
        /** @internal */ this.revealed = new Map();
        /** @internal */ this.undoStack = [];
        /** @internal */ this.redoStack = [];
        /** @internal */ this.undoGroup = null;
        /** @internal */ this.undoHistEl = null;
        /** @internal */ this.chartEl = null;
        /** @internal */ this.calCur = '';
        /** @internal */ this.editing = null;
        /** 사용자 편집기 (열려 있을 때): 편집기·Enter/Tab 으로 확정할 값·열림 번호 */
        /** @internal */ this.ced = null;
        /** @internal */ this.cedWrap = null;
        /** @internal */ this.cedSeq = 0;
        /** 사용자 렌더러가 그린 셀 (destroy 때 치움) */
        /** @internal */ this.custCells = new Set();
        /** 행 사이 검증 오류 (row:col → 메시지) — uniqueBy·maxSum·validateRows */
        /** @internal */ this.rowErrs = new Map();
        // ── 15차: 검증 수준 — 오류(errors·asyncErr·rowErrs)와 따로, 경고·안내 (row:col → 수준·문구). 동기 검증·서버 / 비동기 / 행 사이 ──
        /** @internal */ this.warns = new Map();
        /** @internal */ this.asyncWarn = new Map();
        /** @internal */ this.rowWarns = new Map();
        /** 행 편집(rowEditMode) 버퍼: 물리 행·고친 값·편집 중 값을 보여 주는 Dataset 대리 */
        /** @internal */ this.rowEdit = null;
        /** @internal */ this.rowEditBar = null;
        /** Esc 두 번 되돌리기: 첫 Esc 를 누른 물리 행 (-1 = 없음) */
        /** @internal */ this.escArm = -1;
        /** 바이트 제한 입력: 마지막으로 한도 안이던 편집 글자, 바이트 수 표시 */
        /** @internal */ this.byteGood = '';
        /** @internal */ this.byteEl = null;
        /** 체크 콤보 편집 중 고른 코드 */
        /** @internal */ this.ccSel = null;
        /** 행 추가 위치 (insertPosition before·after): 다음 rowadd 를 보기의 이 위치에 */
        /** @internal */ this.insertAt = null;
        /** appendRowOnDown 으로 마지막에 붙인 행과 그때 값 (손대지 않았으면 더 늘리지 않음) */
        /** @internal */ this.lastAppend = null;
        /** 삭제 행 표시: 보기·그룹 rows → 삭제 행 뺀 rows (집계·내보내기용) */
        /** @internal */ this.liveCache = new WeakMap();
        /** @internal */ this.beforeChangeListeners = new Set();
        /** @internal */ this.beforeInsertListeners = new Set();
        /** @internal */ this.beforeDeleteListeners = new Set();
        /** @internal */ this.beforeCellFocusListeners = new Set();
        /** 경고 확인(warningConfirm)을 받은 경고 목록 서명 — 같은 경고면 다시 묻지 않음 */
        /** @internal */ this.warnAckSig = '';
        /** @internal */ this.rowValTimer = 0;
        /** @internal */ this.errPanelKind = null;
        /** 서버 변경 있음 표시 (실시간 갱신 충돌): 물리 행 → 메시지 */
        /** @internal */ this.srvChanged = new Map();
        /** @internal */ this.composing = false;
        /** @internal */ this.pendingKey = null;
        /** @internal */ this.dropIdx = 0;
        /** @internal */ this.dropItems = [];
        /** @internal */ this.ver = 1;
        /** @internal */ this.first = 0;
        /** @internal */ this.pool = [];
        /** @internal */ this.rafId = 0;
        /** @internal */ this.lastFocusRow = -1;
        /** @internal */ this.dragging = false;
        /** @internal */ this.comboMaps = new WeakMap();
        /** @internal */ this.unsub = () => { };
        /** @internal */ this.ro = null;
        /** @internal */ this.pinBox = [];
        /** @internal */ this.pinPanes = [];
        /** @internal */ this.pinPool = [[], [], [], []];
        /** @internal */ this.sgHead = null; // 고정 그룹: vi → 직전(자기 포함) 그룹 머리행 vi
        /** @internal */ this.sgParent = null; // 그룹 머리행 vi → 상위 그룹 머리행 vi
        /** @internal */ this.sgDisp = null;
        /** 멀티로우: 패인별 너비, 격자 열 너비, 읽는 순서(ci) */
        /** @internal */ this.mlPW = [0, 0, 0];
        /** @internal */ this.mlColW = [];
        /** @internal */ this.navOrd = null;
        /** @internal */ this.mergedEls = new Set(); // 병합 셀: 보이는 영역 가운데로 글자 옮기기(VisibleRect)
        /** @internal */ this.autoCols = false;
        /** 직접 준 옵션 이름 (프리셋을 바꿔도 이것은 유지) */
        /** @internal */ this.explicitKeys = new Set();
        /** @internal */ this.sideBar = null;
        /** @internal */ this.cardView = null;
        /** 트리 지연 로딩 중인 노드 id */
        /** @internal */ this.treeLoading = new Set();
        /** @internal */ this.treeLoaded = new Set();
        /** @internal */ this.treeAllKids = new Map();
        /** @internal */ this.treeMixed = new Set();
        /** 표시 글자 정렬 컬럼에 변환 함수 붙이기 (컬럼 정의·목록이 같으면 같은 함수 → 정렬 순위 캐시 재사용) */
        /** @internal */ this.textSortFns = new Map();
        /** @internal */ this.applyingFilters = false;
        /** @internal */ this.filterSig = '{}';
        /** @internal */ this.pendRows = null;
        /** @internal */ this.rowStateQuiet = false;
        /** @internal */ this.layoutTouched = false;
        /** @internal */ this.layoutGen = 0;
        /** @internal */ this.onPageHide = () => { if (this.layoutTimer)
            this.flushLayout(); };
        /** @internal */ this.layoutTimer = 0;
        /** @internal */ this.layoutLoading = Promise.resolve();
        // ── 서버 그룹·서버 합계 (rowModel: 'server') ──
        /** @internal */ this.srvG = null;
        /** @internal */ this.srvExpandAll = false;
        /** @internal */ this.srvTotals = { sig: '', aggs: null };
        /** 그룹 머리행 rows(또는 전체 view) → 서버 집계 */
        /** @internal */ this.srvAggOf = new WeakMap();
        /** @internal */ this.cardRefreshQ = false;
        // ── 가변 행 높이 (autoRowHeight): offs[vi] = vi 행의 위쪽 y (누적합). null 이면 고정 높이 경로 ──
        /** @internal */ this.offs = null;
        /** @internal */ this.offsVer = -1;
        /** @internal */ this.measureCtx = null;
        /** @internal */ this.charW = new Map();
        // ── 자동 행 높이 캐시: 물리 행 → 잰 높이(0 = 아직). 바뀐 행만 다시 재고 누적합은 그 뒤만 더함 ──
        /** @internal */ this.htCache = null;
        /** @internal */ this.htKey = '';
        /** 다시 잴 행 (셀 편집·되돌리기). null = 전체 다시 (일괄 변경 등) */
        /** @internal */ this.htDirty = new Set();
        /** 물리 행 → vi (증분 갱신용, buildOffsets 때 만듦) */
        /** @internal */ this.viOfRow = null;
        /** 잰 행 수 누계 (시험·진단용) */
        this.measureCount = 0;
        /** @internal */ this.flashUntil = new Map();
        /** @internal */ this.flashTimer = 0;
        // ── 행 높이 직접 조절·행 숨기기 ──
        /** @internal */ this.rowHts = new Map();
        /** @internal */ this.hiddenRowSet = new Set();
        /** @internal */ this.grpCollapsed = new Set();
        /** @internal */ this.grpCollapsedInit = false;
        /** @internal */ this.flexFixed = new Set();
        /** @internal */ this.flexAvail = -1;
        /** @internal */ this.filterTimer = 0;
        // ── 셀 포커스·선택 변경 이벤트 (OnFocusedItemChanged / OnSelectionChanged): 바뀐 것만, 한 동작에 한 번 (마이크로태스크로 모음) ──
        /** @internal */ this.evQueued = false;
        /** @internal */ this.lastCellKey = '';
        /** @internal */ this.lastCell = { row: -1, col: null };
        /** @internal */ this.lastSelSig = '';
        /** @internal */ this.cellFocusListeners = new Set();
        /** @internal */ this.selListeners = new Set();
        /** @internal */ this.pinList = [[], []];
        /** 데이터 막대·색 척도 범위: 규칙의 min/max, 없으면 현재 보기의 최소·최대 (보기·데이터가 바뀌면 다시) */
        /** @internal */ this.ruleStats = new Map();
        /** @internal */ this.ruleStatsView = null;
        /** 정렬·필터용 Dataset: valueGetter 컬럼을 임시 컬럼으로 채운 얕은 사본 (데이터가 바뀌면 다시 계산) */
        /** @internal */ this.qdsCache = null;
        // ── 셀·시트 보호 ──
        /** @internal */ this.protectOn = null;
        /** @internal */ this.cellLock = new Map();
        // ── 빈 새 행 자동 취소 (cancelOnExit) ──
        /** 추가할 때의 값 (물리 행 → 값). 손대지 않았는지 비교용 */
        /** @internal */ this.freshRows = new Map();
        /** @internal */ this.focusGuards = new Set();
        /** @internal */ this.focusListeners = new Set();
        /** @internal */ this.extraRanges = [];
        /** @internal */ this.keepExtra = false;
        /** @internal */ this.selSumKey = '';
        /** 머리글 체크 셈 (캐시): 보이는 행(disp)·컬럼 정의가 그대로면 셀 변경(cell 이벤트) 행만 다시 셈. 그 밖의 자료 변경은 전부 다시 */
        /** @internal */ this.hckCache = new Map();
        /** @internal */ this.hckDirty = new Map();
        /** @internal */ this.hckRecounts = 0; // 전체 다시 셈 횟수 (시험용)
        /** @internal */ this.hckSig = '';
        // ═══════════════════════ 새 항목 행 (NewItemRow) ═══════════════════════
        /** 새 항목 행에서 입력 중인 INSERT 행 (없으면 -1). 확정 전까지 목록(view) 밖 */
        /** @internal */ this.nirRow = -1;
        /** @internal */ this.nirInit = null;
        // ═══════════════════════ 편집 폼 (editMode: inlineForm·modalForm) ═══════════════════════
        /** @internal */ this.form = null;
        /** 정규식 마스크 입력 검사: 맞출 수 없는 글자면 직전 값으로 되돌리고 형식 안내 (QuantumGrid emkRegExprEx 증분 검사) */
        /** @internal */ this.reGood = '';
        // ── 콤보 목록 ──
        /** 콤보 찾기 색인 (목록 배열마다 한 번): 필드별 소문자·초성 — 수만 건도 키 입력마다 다시 만들지 않음 */
        /** @internal */ this.comboIdx = new WeakMap();
        /** @internal */ this.dropCol = null;
        // ── 찾아 바꾸기 (Ctrl+H) ──
        /** @internal */ this.replEl = null;
        /** 다중 컬럼·큰 콤보 목록: 머리글 줄 + 보이는 줄만 (가상 스크롤). 항목은 .og-di[data-i] (클릭 = 고르고 확정) */
        /** @internal */ this.dropBody = null;
        /** @internal */ this.dropCols = null;
        /** 목록 머리글 누름: 그 칸 오름차순 → 내림차순 (고른 항목은 그대로) */
        /** @internal */ this.dropSort = null;
        /** @internal */ this.keyMapSrc = null;
        /** @internal */ this.keyMapN = new Map();
        /** @internal */ this.onMouseUp = () => { this.dragging = false; };
        // ═══════════════════════ 터치 ═══════════════════════
        /** @internal */ this.tch = null;
        /** @internal */ this.lastTap = null;
        /** @internal */ this.noCtxUntil = 0;
        /** @internal */ this.resizing = null;
        /** @internal */ this.nlApplied = false;
        // ═══════════════════════ 클립보드 ═══════════════════════
        /** @internal */ this.copyHeaders = false;
        if (options.csp || options.cspNonce)
            setCspMode(true, options.cspNonce);
        injectCss();
        const given0 = Object.fromEntries(Object.entries(options).filter(([, v]) => v !== undefined));
        // 디자인 유형(프리셋): 묶음 옵션을 밑에 깔고 직접 준 옵션이 이김
        this.explicitKeys = new Set(Object.keys(given0));
        const dflt = Object.fromEntries(Object.entries(OneGrid.defaults ?? {}).filter(([k, v]) => v !== undefined && k !== 'dataset' && k !== 'columns'));
        const given = { ...dflt, ...applyPreset(given0) };
        if (!given.columns) {
            this.autoCols = true;
            given.columns = autoColumns(options.dataset, options.columnLabels);
        }
        this.t = makeT(given.locale, given.messages);
        this.opts = { rowHeight: 28, headerHeight: 30, enterKey: 'next', footerLabel: this.t('합계'), emptyText: this.t('조회된 자료가 없습니다.'), ...given };
        this.root = document.createElement('div');
        this.root.className = 'og';
        this.applyLook();
        this.root.__onegrid = this; // 개발자도구에서 document.querySelector('.og').__onegrid 로 접근
        host.appendChild(this.root);
        this.buildDom();
        if (this.opts.summaryPosition === 'top')
            this.applyLook();
        this.sortKeys = [...(options.sort ?? [])];
        this.setDataset(options.dataset);
        this.initialLayout = this.getLayout();
        if (this.opts.layoutKey && this.opts.layoutStore) {
            // 서버 저장소: 불러오는 동안 사용자가 이미 바꿨으면 덮어쓰지 않는다
            const key = this.opts.layoutKey, gen = this.layoutGen;
            // 창을 닫거나 새로고침할 때 미뤄 둔 저장을 바로 보냄 (지연 600ms 안에 떠나도 잃지 않게)
            if (typeof window !== 'undefined')
                window.addEventListener('pagehide', this.onPageHide);
            this.layoutLoading = this.opts.layoutStore.load(key).then(l => { if (l && gen === this.layoutGen && this.root.isConnected)
                this.applyLayout(l, false); }).catch(() => { });
        }
        else if (this.opts.layoutKey) {
            try {
                const raw = localStorage.getItem('onegrid:' + this.opts.layoutKey);
                if (raw)
                    this.applyLayout(JSON.parse(raw), false);
            }
            catch { /* 저장소를 못 쓰면 기본 화면 */ }
        }
        if (this.opts.noteStore)
            this.reloadNotes();
        this.renderGroupPanel();
        if (this.opts.view === 'card')
            queueMicrotask(() => this.setView('card'));
        if (this.opts.sideBar) {
            this.sideBar = new SideBar(this, this.root, this.t);
            const sb = this.opts.sideBar;
            if (typeof sb === 'object' && sb.open)
                this.sideBar.open(sb.tab);
        }
        // 1.2.0: 숨은 동안(display:none)은 그리지 않고 그리드 밖 팝업을 닫음 → 다시 보일 때(크기 0 → n) 한 번 그림 (작업 반복 없음)
        this.ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => { if (this.isShown())
            this.requestRender(true);
        else
            this.onHidden(); }) : null;
        this.ro?.observe(this.root);
    }
    // ═══════════════════════ 공개 API ═══════════════════════
    setDataset(ds) {
        this.unsub();
        this.ds = ds;
        this.opts.dataset = ds;
        this.unsub = ds.on(e => this.onData(e));
        if (this.opts.keyCol)
            ds.keyCol = this.opts.keyCol;
        this.srm = this.opts.rowModel === 'server' && this.opts.serverSource
            ? new ServerRowModel(ds, this.opts.serverSource, kind => { if (kind === 'block')
                this.bump();
            else {
                this.empty.textContent = this.opts.emptyText ?? '';
                this.empty.hidden = !!this.srm?.error || this.disp.length > 0;
                this.setError(this.srm?.error ?? null, () => this.srm?.retry());
            } })
            : null;
        this.checked.clear();
        this.errors.clear();
        this.undoStack = [];
        this.redoStack = [];
        if (this.autoCols)
            this.opts.columns = autoColumns(ds, this.opts.columnLabels);
        this.setColumns(this.opts.columns, this.opts.headerGroups);
    }
    /** 컬럼 정의 교체. keepLayout: 사용자가 바꾼(또는 저장소에서 불러온) 순서·너비·숨김 등을 새 정의에 다시 입힘 (React 래퍼가 사용) */
    setColumns(columns, headerGroups, opt) {
        if (columns !== this.opts.columns)
            this.autoCols = false; // 직접 컬럼을 주면 자동 생성 끔
        if (opt?.keepLayout && this.layoutTouched)
            columns = this.mergeLayoutColumns(columns, this.getLayout());
        columns = resolveColumnTypes(columns, this.opts.columnTypes);
        columns = columns.map(c => (c.expr && (!c.value || c.__expr !== c.expr) ? Object.assign({ ...c, value: formulaColumn(c.expr) }, { __expr: c.expr }) : c));
        this.opts.columns = columns;
        this.opts.headerGroups = headerGroups;
        for (const c of columns)
            if (!c.value && !this.ds.hasColumn(c.id))
                this.ds.addColumn({ id: c.id, type: (typeof c.type === 'string' && COL_TYPES.has(c.type) ? c.type : 'string') });
        this.layout();
        this.rebuild();
    }
    /** 역할 바꾸기 → 컬럼 표시·편집 권한 다시 적용 (편집 중이면 취소) */
    setRole(role) {
        if (this.editing)
            this.cancelEdit();
        this.opts.role = role;
        this.layout();
    }
    getRole() { return this.opts.role; }
    /** 현재 역할에서 컬럼 권한 */
    columnAccess(col) {
        const d = typeof col === 'string' ? this.opts.columns.find(c => c.id === col) : col;
        return columnAccessOf(d, this.opts.role);
    }
    setOptions(o) {
        // undefined 는 "지정 안 함" — 기본값을 덮어쓰지 않는다 (React props 그대로 넘겨도 안전)
        let given = Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined));
        for (const k of Object.keys(given))
            if (k !== 'preset')
                this.explicitKeys.add(k);
        if ('preset' in given && given.preset !== this.opts.preset) {
            // 프리셋 바꿈: 이전 프리셋에서 온 옵션은 지우고(직접 준 것은 유지) 새 묶음을 깖
            const oldP = getPreset(this.opts.preset)?.options ?? {}, newP = getPreset(given.preset)?.options ?? {};
            const base = {};
            for (const k of Object.keys(oldP))
                if (!this.explicitKeys.has(k) && !(k in newP))
                    base[k] = undefined;
            for (const [k, v] of Object.entries(newP))
                if (!this.explicitKeys.has(k))
                    base[k] = v;
            given = { ...base, ...given };
            for (const k of Object.keys(base))
                if (base[k] === undefined)
                    this.opts[k] = undefined;
            given = Object.fromEntries(Object.entries(given).filter(([, v]) => v !== undefined));
        }
        const changed = (k) => k in given && given[k] !== this.opts[k];
        const relayout = changed('preset') || changed('locale') || changed('messages') || changed('showRowNumber') || changed('showIndicator') || changed('fitColumns') || changed('showRowState') || changed('showCheckbox') || changed('showFilterRow') || changed('showFooter') || changed('rowHeight') || changed('compact') || changed('rowDrag') || changed('dragSource') || changed('touch') || changed('role') || changed('hideGroupedColumns') || changed('showGroupPanel') || ('rowLayout' in given && JSON.stringify(given.rowLayout ?? null) !== JSON.stringify(this.opts.rowLayout ?? null));
        if (changed('pageSize'))
            this.page = 0;
        if (changed('locale') || changed('messages')) {
            const nt = makeT(given.locale ?? this.opts.locale, given.messages ?? this.opts.messages), ot = this.t;
            // 기본 문구를 쓰고 있었으면 새 언어로
            if (this.opts.footerLabel === ot('합계') && !('footerLabel' in given))
                this.opts.footerLabel = nt('합계');
            if (this.opts.emptyText === ot('조회된 자료가 없습니다.') && !('emptyText' in given)) {
                this.opts.emptyText = nt('조회된 자료가 없습니다.');
                this.empty.textContent = this.opts.emptyText;
            }
            this.t = nt;
            this.sideBar?.setTranslate(nt);
        }
        if (changed('noteStore'))
            queueMicrotask(() => this.reloadNotes());
        // 새 항목 행을 끄면 입력 중인 행 정리, 편집 방식을 바꾸면 열린 폼 닫기
        if ('newItemRow' in given && !given.newItemRow && this.opts.newItemRow) {
            if (this.nirDirty())
                this.postNewItemRow(false, false);
            else
                this.cancelNewItemRow();
        }
        if (changed('editMode') && this.form)
            this.closeForm();
        if ('rowEditMode' in given && !given.rowEditMode && this.rowEdit)
            this.saveRowEdit(false); // 행 편집을 끄면 버퍼를 반영
        if (changed('newItemRow'))
            this.requestRender(true);
        if ('saveFilters' in given)
            this.filterSig = JSON.stringify(this.getFilterState());
        if (changed('uniqueBy') || changed('maxSum') || changed('validateRows'))
            queueMicrotask(() => this.schedCrossRows());
        if (changed('preset') || changed('compact') || changed('striped') || changed('highContrast') || changed('touch') || changed('summaryPosition') || changed('theme') || changed('cellStateIcons'))
            queueMicrotask(() => this.applyLook());
        if ('groupBy' in o && JSON.stringify(o.groupBy ?? null) !== JSON.stringify(this.opts.groupBy ?? null)) {
            this.opts.groupBy = o.groupBy ?? null;
            this.groupToggled.clear();
            this.groupCollapseAll = false;
            queueMicrotask(() => this.renderGroupPanel());
        }
        Object.assign(this.opts, given);
        o = given;
        if ('sort' in o)
            this.sortKeys = [...(o.sort ?? [])];
        if (relayout)
            this.layout();
        this.rebuild();
    }
    refresh() { this.rebuild(); }
    /** 선택 범위의 물리 행 (그룹 머리행 제외) */
    getRangeRows() { const { r0, r1 } = this.range(); const out = []; for (let vi = r0; vi <= r1; vi++) {
        const r = this.disp[vi];
        if (r >= 0)
            out.push(r);
    } return out; }
    /** 선택 범위의 컬럼 id */
    getRangeColumns() { const { c0, c1 } = this.range(); return this.cols.slice(c0, c1 + 1).filter(c => !c.special).map(c => c.id); }
    /** 화면 표시 문자열 (콤보 명칭·날짜·숫자 형식 적용) */
    cellText(row, col) { const c = this.colInfo(col); return c ? this.displayText(c, this.valueOf(c, row), row) : String(this.ds.get(row, col) ?? ''); }
    /** 값 (계산·수식 컬럼 포함) */
    cellValue(row, col) { const c = this.colInfo(col); return c ? this.valueOf(c, row) : this.ds.get(row, col); }
    /** @internal */ colInfo(id) {
        const c = this.cols.find(x => x.id === id);
        if (c)
            return c;
        const def = this.opts.columns.find(x => x.id === id);
        return def ? { id, def, ci: -1, pane: 1, left: 0, width: def.width ?? 100 } : undefined;
    }
    /** 현재 화면 순서의 물리 행 번호 (필터·정렬 적용, 그룹 머리행 제외) */
    getViewRows() { return this.view; }
    getFocusedRow() { if (this.focus.vi === NIR)
        return this.nirRow; const it = this.disp[this.focus.vi]; return it == null || it < 0 ? -1 : it; }
    getFocusedColumn() { return this.cols[this.focus.ci]?.id; }
    /** 포커스 행의 키 값 (keyCol 기준) */
    getFocusedKey() { const r = this.getFocusedRow(); return r < 0 ? null : this.ds.keyOf(r); }
    /** 키로 행을 찾아 포커스 (없으면 false) */
    focusByKey(key, col) {
        const r = this.ds.findByKey(key);
        if (r < 0)
            return false;
        const vi = this.disp.indexOf(r);
        if (vi < 0)
            return false;
        const ci = col ? this.cols.findIndex(c => c.id === col) : this.focus.ci;
        this.setFocus(vi, ci < 0 ? this.focus.ci : ci, false);
        return true;
    }
    getCheckedRows() { return [...this.checked].filter(r => this.ds.isVisibleRow(r)); }
    setChecked(rows, on = true) { for (const r of rows)
        on ? this.checked.add(r) : this.checked.delete(r); this.bump(); this.opts.onCheckChange?.({ rows: this.getCheckedRows(), grid: this }); }
    destroy() {
        if (this.ced)
            this.cancelEdit();
        for (const ce of [...this.custCells])
            this.dropCust(ce);
        clearTimeout(this.rowValTimer);
        if (this.layoutTimer)
            this.flushLayout();
        if (typeof window !== 'undefined') {
            window.removeEventListener('pagehide', this.onPageHide);
            window.removeEventListener('mouseup', this.onMouseUp);
        } // mouseup 은 14차 수정 (예전에는 남아 그리드 전체가 GC 안 됨)
        this.closeFilterMenu();
        this.closePopup();
        this.closeFilterBuilder();
        this.clearDetails();
        cancelAnimationFrame(this.rafId);
        this.unsub();
        this.ro?.disconnect();
        this.root.remove();
    }
    // ═══════════════════════ 데이터 → 뷰 ═══════════════════════
    /** @internal */ onData(e) {
        if (e.type === 'load' || e.type === 'commit') {
            if (this.srvChanged.size)
                this.srvChanged.clear();
        }
        if (this.crossOn() || this.rowErrs.size) {
            if (e.type === 'load')
                this.rowErrs.clear();
            if (e.type !== 'state' || e.row == null)
                this.schedCrossRows();
        }
        this.onData0(e);
        if (e.type === 'load' && this.pendRows)
            this.applyRowState(this.pendRows, true); // 자료보다 먼저 온 레이아웃의 행 상태
    }
    /** @internal */ onData0(e) {
        this.ruleStats.clear();
        // 새 항목 행의 INSERT 행: 만들 때는 보기에 넣지 않음. 다시 불러오기·외부에서 지우면 입력 줄 비움
        if (this.nirRow >= 0) {
            if (e.type === 'rowadd' && e.row === this.nirRow) {
                this.bump();
                return;
            }
            if (e.type === 'load' || this.nirRow >= this.ds.length || !this.ds.isVisibleRow(this.nirRow)) {
                if (this.editing?.vi === NIR)
                    this.endEdit();
                this.nirRow = -1;
                this.nirInit = null;
            }
            else if (e.type === 'commit')
                queueMicrotask(() => { if (this.nirRow >= 0 && this.ds.rowState(this.nirRow) === RowState.NORMAL)
                    this.ds.markInserted(this.nirRow); }); // 저장돼도 입력 중인 행은 그대로 새 행
        }
        if (e.type === 'load') {
            this.freshRows.clear();
            if (this.form)
                this.closeForm();
            if (this.rowEdit)
                this.dropRowEdit(false);
            this.lastAppend = null;
            this.warns.clear();
            this.asyncWarn.clear();
            this.rowWarns.clear();
        }
        else if (this.rowEdit && (e.type === 'rowdelete' || e.type === 'batch' || e.type === 'commit') && !this.rowEdit.saving && !this.ds.isVisibleRow(this.rowEdit.row))
            this.dropRowEdit(false); // 편집 중인 행이 지워지면 버퍼도 버림
        if (this.hckCache.size) {
            if (e.type === 'cell' && e.row != null && e.col) {
                const d = this.hckCache.has(e.col) ? this.hckDirty.get(e.col) ?? [] : null; // 머리글 체크 컬럼의 바뀐 행만 모음
                if (d && d.length < 5000) {
                    d.push(e.row);
                    this.hckDirty.set(e.col, d);
                }
                else if (d) {
                    this.hckCache.delete(e.col);
                    this.hckDirty.delete(e.col);
                }
            }
            else if (e.type !== 'state') {
                this.hckCache.clear();
                this.hckDirty.clear();
            }
        }
        // 자동 행 높이 캐시: 바뀐 행만 다시 잼 (불러오기·일괄 변경은 전체)
        if (this.htCache) {
            if (e.type === 'cell' || e.type === 'state')
                this.htInvalidate(e.row ?? -1);
            else if (e.type === 'merge')
                for (const r of e.updated ?? [])
                    this.htInvalidate(r);
            else if (e.type === 'load' || e.type === 'batch')
                this.htInvalidate();
        }
        const t = this.opts.tree;
        if (e.type === 'cell' && this.opts.flashChanges && e.row != null && e.col)
            this.flashCells([{ row: e.row, col: e.col }]);
        if (e.type === 'merge' && this.opts.flashChanges && e.updated?.length)
            this.flashCells(e.updated.map(row => ({ row })));
        if (e.type === 'cell') {
            if (t && (e.col === t.parentCol || e.col === t.orderCol || e.col === t.idCol)) {
                this.rebuild();
                return;
            }
            if (this.cardView)
                this.schedCardRefresh(); // 실행 취소·코드로 바꾼 값도 카드에
            this.bump();
            return;
        }
        if (t && e.type !== 'state') {
            if (e.type === 'load') {
                this.checked.clear();
                this.errors.clear();
                this.undoStack = [];
                this.redoStack = [];
                this.treeInit = false;
                this.treeLoaded.clear();
                this.treeLoading.clear();
                this.treeMixed.clear();
            }
            this.rebuild();
            if (e.type === 'rowadd')
                this.focusNewRow(e.row);
            return;
        }
        if ((e.type === 'rowadd' || e.type === 'rowdelete') && this.extOrder) {
            this.rebuild();
            return;
        }
        // 삭제 행 표시: 삭제(D)는 보기에 남김(다시 그리기만), 삭제 취소(rowadd)는 이미 보기에 있으면 그대로
        if (this.opts.showDeletedRows && e.row != null && (e.type === 'rowdelete' ? this.ds.rowState(e.row) === RowState.DELETE : e.type === 'rowadd' && this.view.includes(e.row))) {
            this.bump();
            return;
        }
        if (e.type === 'rowadd' && !this.isGrouped()) {
            // 새 행은 정렬과 상관없이 맨 아래에 붙인다 (입력 중 행이 튀지 않게). insertPosition before·after 면 포커스 행 앞·뒤
            const at = this.insertAt != null ? Math.max(0, Math.min(this.view.length, this.insertAt)) : this.view.length;
            this.insertAt = null;
            const v = new Uint32Array(this.view.length + 1);
            v.set(this.view.subarray(0, at));
            v[at] = e.row;
            v.set(this.view.subarray(at), at + 1);
            this.view = v;
            this.disp = new Int32Array(v.buffer);
            this.afterView();
            return;
        }
        if (e.type === 'rowdelete' && !this.isGrouped()) {
            const v = this.view.filter(r => r !== e.row);
            this.view = v;
            this.disp = new Int32Array(v.buffer, v.byteOffset, v.length);
            this.afterView();
            return;
        }
        if (e.type === 'state') {
            this.bump();
            return;
        }
        if (e.type === 'merge') {
            // 키 기준 부분 갱신: 정렬·필터는 다시 적용하되 포커스(키)·체크·스크롤은 유지
            const oldRow = this.getFocusedRow(), kc = this.ds.keyCol;
            const key = oldRow >= 0 && kc ? this.ds.keyOf(oldRow) : null;
            for (const r of [...this.checked])
                if (!this.ds.isVisibleRow(r))
                    this.checked.delete(r);
            for (const k of [...this.errors.keys()])
                if (!this.ds.isVisibleRow(+k.split(':')[0]))
                    this.errors.delete(k);
            this.clearAsync(r => !this.ds.isVisibleRow(r));
            this.rebuild();
            const nr = key != null ? this.ds.findByKey(key) : this.ds.isVisibleRow(oldRow) ? oldRow : -1;
            const vi = nr >= 0 ? this.disp.indexOf(nr) : -1;
            if (vi >= 0 && vi !== this.focus.vi) {
                this.focus = { vi, ci: this.focus.ci };
                this.anchor = { ...this.focus };
                this.bump();
                this.emitRowFocus();
            }
            return;
        }
        if (e.type === 'load') {
            this.clearAsync();
            this.pinList = [[], []];
            this.rowHts.clear();
            this.hiddenRowSet.clear();
            this.checked.clear();
            this.errors.clear();
            this.undoStack = [];
            this.redoStack = [];
            this.clearDetails();
            const rn = this.cols.find(c => c.special === 'rownum');
            if (rn && rn.width !== Math.max(40, String(this.ds.length).length * 8 + 14))
                this.layout();
        }
        if (e.type === 'commit') {
            const keep = (x) => !x.rows.length, strip = (l) => l.filter(keep).map(x => (x.order ? { ...x, order: undefined } : x)).filter(x => x.cells.length);
            this.undoStack = strip(this.undoStack);
            this.redoStack = strip(this.redoStack);
        } // 저장 후 행 추가·삭제는 되돌릴 수 없음
        if (e.type === 'batch' || e.type === 'commit') {
            // 행 구성이 바뀌지 않았으면 순서 유지, 삭제된 행만 뺀다
            const sd = !!this.opts.showDeletedRows, keepRow = (r) => (sd ? this.ds.rowState(r) !== RowState.DISCARDED : this.ds.isVisibleRow(r));
            const v = this.view.filter(keepRow);
            const added = [];
            if (!this.isGrouped()) {
                const inView = new Set(v);
                for (let r = 0; r < this.ds.length; r++)
                    if (keepRow(r) && !inView.has(r) && r !== this.nirRow)
                        added.push(r);
            }
            if (this.isGrouped() || this.extOrder) {
                this.rebuild();
                return;
            }
            const nv = new Uint32Array(v.length + added.length);
            nv.set(v);
            nv.set(added, v.length);
            this.view = nv;
            this.disp = new Int32Array(nv.buffer);
            this.afterView();
            return;
        }
        this.rebuild();
    }
    /** 필터만 적용한 행 (정렬 전). exceptMenuCol 의 헤더 필터는 빼고 — 필터 메뉴 값 목록용 */
    /** @internal */ filteredRows(exceptMenuCol) {
        let v = this.ds.allRows(!!this.opts.showDeletedRows);
        if (this.nirRow >= 0) {
            const nr = this.nirRow;
            v = v.filter(r => r !== nr);
        } // 새 항목 행에서 입력 중인 행은 확정 전까지 목록 밖
        if (this.hiddenRowSet.size)
            v = v.filter(r => !this.hiddenRowSet.has(r));
        const preds = [...this.extFilters];
        const qd = this.queryDs();
        if (this.filterEnabled)
            for (const [col, text] of this.filterText) {
                const c = this.cols.find(x => x.id === col);
                if (!c)
                    continue;
                const p = parseFilterText(col, this.comboFilterValue(c, text), qd.cols[col]?.kind === 'num');
                if (p)
                    preds.push(p);
            }
        if (preds.length)
            v = filterView(qd, v, preds);
        if (this.filterEnabled) {
            v = this.applyMenuFilters(v, exceptMenuCol);
            if (this.advFilter) {
                let test;
                try {
                    test = compileFilter(this.queryDs(), this.advFilter);
                }
                catch {
                    test = () => true;
                }
                v = v.filter(test);
            }
        }
        if (this.quickText) {
            const ids = this.cols.filter(c => !c.special && !c.def.value && !c.def.privacy).map(c => c.id);
            v = quickSearch(this.ds, v, this.quickText, ids, (col, raw) => this.searchText(col, raw));
        }
        if (this.findText && this.findMode === 'filter')
            v = v.filter(this.findRowTest(parseFind(this.findText)));
        return v;
    }
    /** @internal */ isFiltering() {
        return this.extFilters.length > 0 || !!this.quickText || (this.findMode === 'filter' && !!this.findText) || (this.filterEnabled && (this.filterText.size > 0 || this.menuFilters.size > 0 || !!this.advFilter));
    }
    /** @internal */ applyMenuFilters(v, except) {
        for (const [col, f] of this.menuFilters) {
            if (col === except)
                continue;
            const qd = this.queryDs(), dc = qd.cols[col];
            if (!dc)
                continue;
            if (f.cond)
                v = filterView(qd, v, [{ col, ...f.cond }]);
            if (!f.values)
                continue;
            const keep = f.values;
            const out = new Uint32Array(v.length);
            let k = 0;
            if (dc instanceof StrColumn) {
                const mask = new Uint8Array(dc.dict.length);
                for (let i = 0; i < dc.dict.length; i++)
                    mask[i] = keep.has(dc.dict[i] ?? '') ? 1 : 0;
                for (let i = 0; i < v.length; i++)
                    if (mask[dc.codes[v[i]]])
                        out[k++] = v[i];
            }
            else {
                const vals = dc.vals;
                const nums = new Set([...keep].filter(x => x !== '').map(Number)), empty = keep.has('');
                for (let i = 0; i < v.length; i++) {
                    const x = vals[v[i]];
                    if (x !== x ? empty : nums.has(x))
                        out[k++] = v[i];
                }
            }
            v = out.slice(0, k);
        }
        return v;
    }
    /** @internal */ rebuild() {
        if (!this.ds)
            return;
        if (this.srm) {
            // 서버 모드: 정렬·필터는 서버에서. 조건이 바뀌면 처음부터 다시 받는다. 합계 줄은 서버 집계
            const q = this.getServerQuery(), gcols = this.groupCols();
            this.fetchServerTotals(q);
            if (gcols.length) {
                this.rebuildServerGroups(q, gcols);
                return;
            }
            const wasGrouped = !!this.srvG;
            this.srvG = null;
            if (this.srm.setQuery(q, wasGrouped)) {
                this.bodyPanes[1].scrollTop = 0;
                this.focus = { vi: 0, ci: this.focus.ci };
                this.srvDistinct.clear();
            }
            const v = this.ds.allRows();
            this.view = v;
            this.disp = new Int32Array(v.buffer, v.byteOffset, v.length);
            this.groups = [];
            this.afterFilterChange();
            this.afterView();
            return;
        }
        let v = this.filteredRows();
        this.afterFilterChange();
        if (this.extOrder) { // 밖에서 정한 순서 (소계 줄 포함). 그리드 필터·삭제 행은 뺌
            const keep = this.isFiltering() ? new Set(v) : null;
            const out = this.extOrder.items.filter(x => x < 0 || ((!keep || keep.has(x)) && x < this.ds.length && this.ds.isVisibleRow(x)));
            this.view = Uint32Array.from(out.filter(x => x >= 0));
            this.disp = out;
            this.groups = [];
            this.afterView();
            return;
        }
        if (this.opts.tree) {
            this.buildTree(v, this.isFiltering());
            return;
        }
        const gcols = this.groupCols();
        const gsk = this.opts.groupSort?.by === 'key' ? this.opts.groupSort.dir : null;
        const keys = gcols.length ? [...gcols.map(c => ({ col: c, dir: gsk ?? this.groupDir.get(c) ?? 'asc' })), ...this.sortKeys.filter(k => !gcols.includes(k.col))] : this.sortKeys;
        const qd = this.queryDs();
        if (keys.length)
            v = sortView(qd, v, this.withSortText(keys.filter(k => qd.cols[k.col])));
        this.view = v;
        if (gcols.length) {
            const levels = gcols.map(col => this.groupLevel(col));
            const order = this.groupOrder();
            this.groupArgs = { levels, order };
            const r = buildGroupsMulti(this.ds, v, levels, path => this.groupCollapseAll !== this.groupToggled.has(path), order);
            this.disp = r.disp;
            this.groups = r.groups;
        }
        else {
            this.disp = new Int32Array(v.buffer, v.byteOffset, v.length);
            this.groups = [];
        }
        this.afterView();
    }
    /** @internal */ afterView(keep = false) {
        if (this.cardView)
            queueMicrotask(() => this.cardView?.refresh());
        if (this.sideBar?.isOpen())
            queueMicrotask(() => this.sideBar?.refresh());
        if (!keep) {
            this.baseDisp = this.disp;
            this.disp = this.postDisp(this.disp);
        }
        if (this.focus.vi >= this.disp.length)
            this.focus.vi = Math.max(0, this.disp.length - 1);
        // 자료가 없으면 새 항목 행으로 (바로 입력)
        const no = this.nirOpt();
        if (no && !this.disp.length && this.focus.vi !== NIR)
            this.focus = { vi: NIR, ci: this.firstNirCol() };
        else if (!no && this.focus.vi === NIR)
            this.focus = { vi: 0, ci: this.focus.ci };
        this.renderPager();
        this.anchor = { ...this.focus };
        this.empty.hidden = this.disp.length > 0;
        this.empty.textContent = this.opts.emptyText ?? '';
        this.root.setAttribute('aria-rowcount', String(this.disp.length + 1));
        this.root.setAttribute('aria-colcount', String(this.cols.length));
        this.buildOffsets();
        this.sizer.style.height = Math.min(this.totalH(), MAX_SCROLL_PX) + 'px';
        // 행이 줄면 스크롤 위치를 결과 범위 안으로 (빈 화면 방지)
        const main = this.bodyPanes[1], maxTop = Math.max(0, Math.min(this.totalH(), MAX_SCROLL_PX) - main.clientHeight);
        if (main.scrollTop > maxTop)
            main.scrollTop = maxTop;
        this.renderFooter();
        this.renderFilterPanel();
        this.opts.onViewChange?.({ rows: this.view.length, total: this.ds.length, grid: this });
        this.bump();
        this.emitRowFocus();
    }
    /** disp 후처리: 펼친 디테일마다 슬롯 하나를 데이터 행 뒤에 끼움 (높이는 buildOffsets 에서 디테일 높이 그대로) */
    /** @internal */ postDisp(base) {
        this.extras = [];
        this.rowNo = null;
        let out = base, off = 0;
        if (this.extOrder)
            out = out.map(v => (v < 0 && v > EXTRA ? (this.extras.push({ kind: 'sub', row: -1, n: 1, k: -v - 1 }), EXTRA - (this.extras.length - 1)) : v));
        if (this.opts.groupFooter && this.groups.length)
            out = withGroupFooters(out, this.groups, gi => { this.extras.push({ kind: 'gfoot', row: -1, n: 1, g: this.groups[gi] }); return EXTRA - (this.extras.length - 1); });
        // 페이지: 데이터 행(접힌 그룹 머리행 포함)만 세어 disp 를 자름 — 그룹 머리행·푸터는 세지 않음
        const ps = Math.max(0, this.opts.pageSize ?? 0) | 0;
        const pr = pageRange(out, ps, this.page, v => v >= 0 || (v > EXTRA && !!this.groups[-v - 1]?.collapsed));
        this.pageTotal = pr.total;
        this.pageCount = pr.pages;
        this.page = pr.page;
        if (ps) {
            off = pr.start;
            out = out.subarray(pr.start, pr.end);
        }
        const det = this.opts.detail && this.detailOpen.size ? 1 : 0;
        const frow = this.form?.mode === 'inline' ? this.form.row : -1;
        if (det || frow >= 0) {
            let add = 0;
            for (let i = 0; i < out.length; i++) {
                if (det && out[i] >= 0 && this.detailOpen.has(out[i]))
                    add += det;
                if (frow >= 0 && out[i] === frow)
                    add++;
            }
            if (add) {
                const src = out, no = new Int32Array(src.length + add);
                out = new Int32Array(src.length + add);
                let j = 0;
                for (let i = 0; i < src.length; i++) {
                    const it = src[i];
                    no[j] = i + off;
                    out[j++] = it;
                    if (det && it >= 0 && this.detailOpen.has(it)) {
                        const x = EXTRA - this.extras.length;
                        this.extras.push({ kind: 'detail', row: it, n: det });
                        for (let k = 0; k < det; k++) {
                            no[j] = i + off;
                            out[j++] = x;
                        }
                    }
                    if (it === frow && frow >= 0) {
                        const x = EXTRA - this.extras.length;
                        this.extras.push({ kind: 'form', row: it, n: 1 });
                        no[j] = i + off;
                        out[j++] = x;
                    } // 편집 폼 (행 아래 펼침)
                }
                this.rowNo = no;
            }
        }
        if (off && !this.rowNo) {
            const no = new Int32Array(out.length);
            for (let i = 0; i < no.length; i++)
                no[i] = i + off;
            this.rowNo = no;
        }
        return out;
    }
    /** 후처리만 다시 (디테일 펼침 등). 포커스 행 유지 */
    /** @internal */ refreshDisp() {
        const row = this.getFocusedRow();
        this.disp = this.postDisp(this.baseDisp);
        if (row >= 0) {
            const vi = this.disp.indexOf(row);
            if (vi >= 0)
                this.focus.vi = vi;
        }
        this.ver++;
        this.afterView(true);
    }
    // ═══════════════════════ 레이아웃 ═══════════════════════
    /** 한 줄 높이. rh 는 레코드 높이 (멀티로우면 줄 수 배) */
    /** @internal */ get lineH() { const h = this.opts.compact ? Math.min(22, this.opts.rowHeight) : this.opts.rowHeight; return this.touchOn ? Math.max(36, h) : h; }
    /** 터치 모드인지 (touch: true, 또는 'auto' 이고 굵은 포인터 기기) */
    get touchOn() { const t = this.opts.touch; return t === true || (t === 'auto' && typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches); }
    /** @internal */ get lines() { const l = this.opts.rowLayout; return l && l.rows > 0 ? l.rows | 0 : 1; }
    /** @internal */ get rh() { return this.lineH * this.lines; }
    /** @internal */ rowTop(vi) { return this.offs ? this.offs[Math.max(0, Math.min(vi, this.disp.length))] : vi * this.rh; }
    /** @internal */ rowH(vi) { return this.offs ? this.offs[vi + 1] - this.offs[vi] : this.rh; }
    /** @internal */ totalH() { return this.offs ? this.offs[this.disp.length] : this.disp.length * this.rh; }
    /** y 위치의 행 (이진 탐색) */
    /** @internal */ viAt(y) {
        const o = this.offs;
        if (!o)
            return Math.floor(y / this.rh);
        let lo = 0, hi = this.disp.length - 1;
        while (lo < hi) {
            const m = (lo + hi + 1) >> 1;
            if (o[m] <= y)
                lo = m;
            else
                hi = m - 1;
        }
        return Math.max(0, lo);
    }
    /** 글자 단위 줄바꿈(word-break:break-all)으로 줄 수 계산 */
    /** @internal */ wrapLines(text, avail, maxLines) {
        if (!text)
            return 1;
        if (!this.measureCtx) {
            this.measureCtx = document.createElement('canvas').getContext('2d');
            const cs = getComputedStyle(this.root);
            if (this.measureCtx)
                this.measureCtx.font = `${cs.fontSize || '12px'} ${cs.fontFamily || 'sans-serif'}`;
        }
        const ctx = this.measureCtx;
        let lines = 1, w = 0;
        for (const ch of text) {
            if (ch === '\n') {
                lines++;
                w = 0;
                if (lines >= maxLines)
                    return maxLines;
                continue;
            }
            let cw = this.charW.get(ch);
            if (cw == null) {
                cw = ctx ? ctx.measureText(ch).width : 7;
                this.charW.set(ch, cw);
            }
            if (w + cw > avail && w > 0) {
                lines++;
                w = cw;
                if (lines >= maxLines)
                    return maxLines;
            }
            else
                w += cw;
        }
        return lines;
    }
    /** @internal */ htInvalidate(row) {
        if (!this.htCache)
            return;
        if (row == null || row < 0) {
            this.htCache = null;
            this.htDirty = null;
            return;
        }
        if (row < this.htCache.length)
            this.htCache[row] = 0;
        this.htDirty?.add(row);
    }
    /** @internal */ wrapCols() { return this.opts.autoRowHeight && !this.opts.rowLayout ? this.cols.filter(c => c.def.wrap && !c.special) : []; }
    /** 행 하나의 자동 높이 (캐시) */
    /** @internal */ autoH(row, wcols, maxL) {
        const key = wcols.map(c => c.id + ':' + c.width).join() + '|' + maxL + '|' + this.rh;
        let cache = this.htCache;
        if (!cache || this.htKey !== key) {
            cache = this.htCache = new Float32Array(Math.max(16, this.ds.length));
            this.htKey = key;
        }
        else if (row >= cache.length) {
            const c2 = new Float32Array(Math.max(row + 1, cache.length * 2));
            c2.set(cache);
            cache = this.htCache = c2;
        }
        if (cache[row] > 0)
            return cache[row];
        let lines = 1;
        for (const c of wcols) {
            const t = this.displayText(c, this.valueOf(c, row), row);
            if (t)
                lines = Math.max(lines, this.wrapLines(t, c.width - 13, maxL));
        }
        this.measureCount++;
        return (cache[row] = lines > 1 ? Math.max(this.rh, lines * WRAP_LINE_PX + 8) : this.rh);
    }
    /** 가변 높이 누적합 다시 만들기 (뷰가 바뀌거나 데이터가 바뀐 뒤). 잰 높이는 캐시에서 */
    /** @internal */ buildOffsets() {
        const ml = !!this.opts.rowLayout;
        const wcols = this.wrapCols();
        const fixedH = ml ? null : this.rowHts.size ? this.rowHts : null;
        // 멀티로우 그룹 머리행·그룹 푸터는 한 줄 높이 (레코드 높이 아님)
        const mlg = ml && this.lines > 1 && this.disp.some(v => v < 0);
        const det = this.extras.some(x => x.kind === 'detail' || x.kind === 'form');
        if (!wcols.length) {
            this.htCache = null;
            this.htKey = '';
        }
        if (!wcols.length && !fixedH && !mlg && !det) {
            this.offs = null;
            this.viOfRow = null;
            return;
        }
        const n = this.disp.length, o = new Float64Array(n + 1), rh = this.rh, sh = mlg ? this.lineH : rh;
        const maxL = Math.max(1, this.opts.autoRowHeightMaxLines ?? 6);
        const inv = wcols.length ? new Int32Array(Math.max(16, this.ds.length)).fill(-1) : null;
        for (let vi = 0; vi < n; vi++) {
            const row = this.disp[vi];
            if (row < 0) {
                const x = det && row <= EXTRA ? this.extras[EXTRA - row] : null;
                o[vi + 1] = o[vi] + (x?.kind === 'detail' ? this.getDetailHeight(x.row) : x?.kind === 'form' ? Math.max(this.rh, this.form?.h ?? this.rh * 3) : sh);
                continue;
            }
            if (inv && row < inv.length)
                inv[row] = vi;
            const fh = fixedH ? fixedH.get(row) : undefined;
            o[vi + 1] = o[vi] + (fh != null ? fh : wcols.length ? this.autoH(row, wcols, maxL) : rh);
        }
        this.offs = o;
        this.offsVer = this.ds.version;
        this.viOfRow = inv;
        this.htDirty = new Set();
    }
    /** 데이터만 바뀐 뒤 (보기 그대로): 바뀐 행만 다시 재고 누적합을 고침. 못 하면 false */
    /** @internal */ updateOffsets() {
        const o = this.offs, inv = this.viOfRow, dirty = this.htDirty, wcols = this.wrapCols();
        if (!o || !inv || !dirty || !dirty.size || !wcols.length || !this.htCache)
            return false;
        const maxL = Math.max(1, this.opts.autoRowHeightMaxLines ?? 6), ch = [];
        for (const row of dirty) {
            const vi = row < inv.length ? inv[row] : -1;
            if (vi < 0 || this.disp[vi] !== row)
                continue;
            if (this.rowHts.has(row))
                continue;
            const d = this.autoH(row, wcols, maxL) - (o[vi + 1] - o[vi]);
            if (d)
                ch.push({ vi, d });
        }
        applyRowDeltas(o, ch);
        this.offsVer = this.ds.version;
        this.htDirty = new Set();
        return true;
    }
    // ── 행 클래스·변경 강조 ──
    /** @internal */ rowClassOf(row) {
        let out = '';
        const f = this.opts.rowClass?.(row, this.ds);
        if (f)
            out += ' ' + f;
        const rules = this.opts.rowClassRules;
        if (rules)
            for (const k in rules) {
                try {
                    if (rules[k](row, this.ds))
                        out += ' ' + k;
                }
                catch { /* 규칙 오류는 무시 */ }
            }
        return out;
    }
    /** @internal */ applyLook() {
        // 합계 줄 위치: 'top' 이면 머리글 바로 아래(본문 위)로 옮김
        const top = this.opts.summaryPosition === 'top', bw = this.bodyPanes?.[1]?.parentElement;
        if (!bw || !this.foot) { /* 생성 중 (buildDom 전) */ }
        else if (top && this.foot.nextElementSibling !== bw)
            this.root.insertBefore(this.foot, bw);
        else if (!top && (bw.compareDocumentPosition(this.foot) & Node.DOCUMENT_POSITION_PRECEDING)) {
            const fp = this.root.querySelector(':scope > .og-fpanel');
            this.root.insertBefore(this.foot, fp ? fp.nextSibling : null);
        }
        this.root.classList.toggle('og-foot-top', top);
        this.root.classList.toggle('og-rowrs', !!this.opts.rowResize);
        this.root.classList.toggle('og-touch', this.touchOn);
        this.root.classList.toggle('og-compact', !!this.opts.compact);
        this.root.classList.toggle('og-nostripe', this.opts.striped === false);
        this.root.classList.toggle('og-hc-theme', !!this.opts.highContrast);
        this.root.classList.toggle('og-sicons', this.opts.cellStateIcons !== false);
        const th = this.opts.theme;
        if (th) {
            if (this.root.getAttribute('data-og-theme') !== th)
                this.root.setAttribute('data-og-theme', th);
        }
        else if (this.root.hasAttribute('data-og-theme'))
            this.root.removeAttribute('data-og-theme');
    }
    /** @internal */ layout() {
        this.offsVer = -1; // 컬럼 너비가 바뀌면 줄바꿈 높이도 다시
        const specials = [];
        const mk = (id, special, width, header) => ({ id, special, def: { id, header, width, align: 'center' }, ci: 0, pane: 0, left: 0, width });
        if (this.opts.showIndicator)
            specials.push(mk('__ind', 'indicator', this.touchOn ? 28 : 20, ''));
        if (this.opts.detail)
            specials.push(mk('__detail', 'detail', 22, ''));
        if (this.opts.rowDrag || this.opts.dragSource)
            specials.push(mk('__drag', 'drag', this.touchOn ? 32 : 18, ''));
        if (this.opts.showRowState)
            specials.push(mk('__state', 'state', 26, ''));
        if (this.opts.showRowNumber)
            specials.push(mk('__rownum', 'rownum', Math.max(40, String(this.ds?.length ?? 0).length * 8 + 14), 'No'));
        if (this.opts.showCheckbox)
            specials.push(mk('__check', 'check', this.touchOn ? 44 : 30, ''));
        const grouped = this.opts.hideGroupedColumns ? new Set(this.groupCols()) : null;
        if (!this.grpCollapsedInit) {
            this.grpCollapsedInit = true;
            const w = (gs) => { for (const g of gs ?? [])
                if (typeof g !== 'string') {
                    if (g.collapsible && g.collapsed)
                        this.grpCollapsed.add(g.header);
                    w(g.children);
                } };
            w(this.opts.headerGroups);
        }
        const byGroup = this.grpCollapsed.size ? collapsedHidden(this.opts.headerGroups, this.grpCollapsed) : null;
        const defs = this.opts.columns.filter(c => !c.hidden && !grouped?.has(c.id) && !byGroup?.has(c.id) && this.columnAccess(c) !== 'hidden');
        const isLeft = (c) => c.pinned === true || c.pinned === 'left';
        const mk2 = (d, pane) => ({ id: d.id, def: d, ci: 0, pane, left: 0, width: d.width ?? 100 });
        const leftCols = [...specials, ...defs.filter(isLeft).map(d => mk2(d, 0))];
        const mainCols = defs.filter(c => !c.pinned).map(d => mk2(d, 1));
        const rightCols = defs.filter(c => c.pinned === 'right').map(d => mk2(d, 2));
        const rl = this.opts.rowLayout;
        if (rl && rl.cells?.length) { // 멀티로우: 특수 컬럼(왼쪽) → 왼쪽 고정 셀 → 스크롤 셀 → 오른쪽 고정 셀, 패인 안은 줄·열 순(읽는 순서)
            const byId = new Map(this.opts.columns.filter(d => this.columnAccess(d) !== 'hidden').map(d => [d.id, d]));
            const pl = placeRowLayout({ ...rl, cells: rl.cells.filter(x => byId.has(x.col)) }, id => byId.get(id)?.width, id => { const p = byId.get(id)?.pinned; return p === true || p === 'left' ? 'left' : p === 'right' ? 'right' : undefined; });
            this.mlPW = pl.paneWidths;
            this.mlColW = pl.colWidths;
            const cells = pl.cells.map(x => ({ ...mk2(byId.get(x.col), x.pane), lt: x.row, lh: x.rowSpan, lc: x.colIndex, ls: x.colSpan, pl: x.pl, gx: x.left, left: x.pl, width: x.width }));
            this.cols = [...specials, ...[0, 1, 2].flatMap(p => cells.filter(c => c.pane === p))];
            // 읽는 순서 (←→·Tab): 특수 컬럼, 그다음 줄·가로 위치 순
            const ord = this.cols.map((_, i) => i);
            this.navOrd = ord.sort((a, b) => { const x = this.cols[a], y = this.cols[b]; return x.lh == null || y.lh == null ? (x.lh == null ? (y.lh == null ? a - b : -1) : 1) : x.lt - y.lt || x.gx - y.gx; });
        }
        else {
            this.cols = [...leftCols, ...mainCols, ...rightCols];
            this.navOrd = null;
        }
        this.recalcWidths();
        if (this.focus.ci >= this.cols.length)
            this.focus.ci = 0;
        this.headRows = this.opts.headerGroups?.length ? headerChains(this.opts.headerGroups).depth + 1 : 1;
        this.renderHeader();
        this.pool.forEach(p => p.els.forEach(e => e.remove()));
        this.pool = [];
        this.mergedEls.clear();
        this.pinPool.forEach(ps => ps.forEach(p => p.els.forEach(e => e.remove())));
        this.pinPool = [[], [], [], []];
        this.ver++;
    }
    /** @internal */ fitMode() { return this.opts.fitColumns ?? (this.opts.columns.some(c => c.flex) ? 'flex' : 'none'); }
    /** 비율 너비 적용 (fitColumns). 본문 너비에서 세로 스크롤 막대를 뺀 만큼을 채운다 */
    /** @internal */ applyFlex() {
        const mode = this.fitMode();
        if (mode === 'none' || this.opts.rowLayout || !this.root.isConnected)
            return;
        const bw = this.bodyPanes[1].parentElement, main = this.bodyPanes[1];
        const avail = bw.clientWidth - (main.offsetWidth - main.clientWidth);
        this.flexAvail = bw.clientWidth;
        if (avail <= 0)
            return;
        const spec = this.cols.map(c => {
            const fixed = !!c.special || this.flexFixed.has(c.id);
            const flex = fixed ? 0 : mode === 'all' ? (c.def.flex ?? c.def.width ?? 100) : c.def.flex ?? 0;
            return { width: fixed || !flex ? c.width : c.def.width ?? 100, flex, min: c.def.minWidth, max: c.def.maxWidth };
        });
        fitWidths(spec, avail).forEach((w, i) => { this.cols[i].width = w; });
    }
    /** 컨테이너 너비가 바뀌었으면 비율 너비 다시 (render 에서 호출) */
    /** @internal */ refitIfResized() {
        if (this.fitMode() === 'none' || this.opts.rowLayout)
            return;
        const bw = this.bodyPanes[1].parentElement;
        if (bw.clientWidth === this.flexAvail)
            return;
        this.recalcWidths();
        this.renderHeader();
        if (this.opts.showFooter)
            this.renderFooter();
        this.pool.forEach(p => { p.key = ''; p.cells.forEach(a => a.forEach(x => (x.ci = -1))); });
    }
    /** @internal */ recalcWidths() {
        this.applyFlex();
        const x = [0, 0, 0];
        const ml = this.opts.rowLayout && this.cols.some(c => c.lc != null);
        this.cols.forEach((c, i) => { c.ci = i; if (ml && c.lc != null)
            return; c.left = x[c.pane]; x[c.pane] += c.width; });
        if (ml) { // 멀티로우: 왼쪽 고정 셀은 특수 컬럼 뒤에
            const sw = x[0];
            for (const c of this.cols)
                if (c.lc != null)
                    c.left = (c.pane === 0 ? sw : 0) + c.pl;
            x[0] = sw + this.mlPW[0];
            x[1] = this.mlPW[1];
            x[2] = this.mlPW[2];
        }
        this.paneWidth = x;
        const lw = x[0] + 'px', rw = x[2] + 'px';
        for (const ps of [this.headPanes, this.bodyPanes, this.footPanes, ...this.pinPanes]) {
            ps[0].style.width = lw;
            ps[1].style.left = lw;
            ps[1].style.right = rw;
            ps[2].style.width = rw;
        }
        this.bodyPanes[0].style.display = x[0] ? '' : 'none';
        this.bodyPanes[2].style.display = this.headPanes[2].style.display = this.footPanes[2].style.display = x[2] ? '' : 'none';
        this.headInner.style.width = this.footInner.style.width = x[1] + 'px';
        for (const ps of this.pinPanes) {
            ps[2].style.display = x[2] ? '' : 'none';
            ps[1].firstElementChild.style.width = x[1] + 'px';
        }
        this.sizer.style.width = x[1] + 'px';
    }
    /** @internal */ buildDom() {
        const r = this.root;
        setHtml(r, `<div class="og-gpanel" hidden></div><div class="og-head"><div class="og-hp og-hl"></div><div class="og-hp og-hm"><div class="og-hi"></div></div><div class="og-hp og-hr"></div></div>
<div class="og-pin og-pt" hidden><div class="og-hp og-hl"></div><div class="og-hp og-hm"><div class="og-hi"></div></div><div class="og-hp og-hr"></div></div>
<div class="og-bw"><div class="og-bp og-bl"><div class="og-layer"></div></div><div class="og-bp og-bm"><div class="og-sizer"><div class="og-layer"></div></div></div><div class="og-bp og-br"><div class="og-layer"></div></div><div class="og-empty" hidden></div><div class="og-dl"></div><div class="og-fh" hidden title="${this.t('끌어서 채우기')}"></div><div class="og-pin og-sg" hidden><div class="og-hp og-hl"></div><div class="og-hp og-hm"><div class="og-hi"></div></div><div class="og-hp og-hr"></div></div></div>
<div class="og-pin og-pb" hidden><div class="og-hp og-hl"></div><div class="og-hp og-hm"><div class="og-hi"></div></div><div class="og-hp og-hr"></div></div>
<div class="og-pin og-nir" hidden><div class="og-hp og-hl"></div><div class="og-hp og-hm"><div class="og-hi"></div></div><div class="og-hp og-hr"></div></div>
<div class="og-fpanel" hidden></div>
<div class="og-foot" hidden><div class="og-hp og-hl"></div><div class="og-hp og-hm"><div class="og-hi"></div></div><div class="og-hp og-hr"></div></div><div class="og-pager" hidden role="navigation" aria-label="${this.t('페이지')}"></div><div class="og-selsum" hidden role="status" aria-live="polite"></div>
<textarea class="og-ime" rows="1" autocomplete="off" autocorrect="off" spellcheck="false" aria-label="${this.t('셀 입력')}"></textarea><div class="og-drop" hidden role="listbox"></div><div class="og-cal" hidden role="dialog" aria-label="${this.t('달력')}"></div><div class="og-tip" hidden role="alert"></div>`);
        this.head = r.querySelector('.og-head');
        this.headPanes = [...this.head.children];
        this.gpanel = r.querySelector('.og-gpanel');
        this.headInner = this.headPanes[1].firstElementChild;
        const bw = r.querySelector('.og-bw');
        this.bodyPanes = [bw.children[0], bw.children[1], bw.children[2]];
        this.layers = [this.bodyPanes[0].firstElementChild, bw.querySelector('.og-bm .og-layer'), this.bodyPanes[2].firstElementChild];
        this.sizer = bw.querySelector('.og-sizer');
        this.empty = bw.querySelector('.og-empty');
        this.detailLayer = bw.querySelector('.og-dl');
        this.fh = bw.querySelector('.og-fh');
        this.fh.addEventListener('mousedown', e => this.startFill(e));
        // 터치: 채우기 손잡이 끌기
        this.fh.addEventListener('touchstart', e => { e.stopPropagation(); if (e.touches.length === 1)
            this.touchDrag(e, this.fh, false); }, { passive: false });
        // 디테일 위 휠은 (안쪽 그리드가 아니면) 본문 스크롤로
        this.detailLayer.addEventListener('wheel', e => { if (!e.target.closest('.og-dd .og-bp'))
            this.bodyPanes[1].scrollTop += e.deltaY; });
        this.foot = r.querySelector('.og-foot');
        this.foot.addEventListener('contextmenu', e => this.onFooterContext(e));
        this.fpanel = r.querySelector('.og-fpanel');
        this.fpanel.addEventListener('click', e => {
            const a = e.target.closest('[data-a]');
            if (!a)
                return;
            if (a.dataset.a === 'edit')
                this.openFilterBuilder();
            else if (a.dataset.a === 'clear') {
                this.menuFilters.clear();
                this.advFilter = null;
                this.filterText.clear();
                this.filterEnabled = true;
                this.syncFilterInputs();
                this.renderHeader();
                this.rebuild();
            }
        });
        this.fpanel.addEventListener('change', e => {
            const t = e.target;
            if (t.dataset.a === 'toggle')
                this.setFilterEnabled(t.checked);
            else if (t.dataset.a === 'mru' && t.value !== '')
                this.applyRecentFilter(+t.value);
        });
        this.footPanes = [...this.foot.children];
        this.pager = r.querySelector('.og-pager');
        this.pager.addEventListener('click', e => { const b = e.target.closest('button[data-p]'); if (b)
            this.setPage(+b.dataset.p); });
        this.pager.addEventListener('change', e => { this.page = 0; this.setOptions({ pageSize: +e.target.value }); });
        this.pinBox = [r.querySelector('.og-pt'), r.querySelector('.og-pb'), r.querySelector('.og-sg'), r.querySelector('.og-nir')];
        this.pinPanes = this.pinBox.map(b => [...b.children]);
        this.nirInfo = document.createElement('div');
        this.nirInfo.className = 'og-nirinfo';
        this.nirInfo.setAttribute('aria-hidden', 'true');
        this.pinBox[3].appendChild(this.nirInfo);
        this.pinBox[3].setAttribute('role', 'row');
        this.pinBox[3].setAttribute('aria-label', this.t('새 항목 행'));
        for (const b of this.pinBox) {
            b.addEventListener('mousedown', e => this.onMouseDown(e));
            b.addEventListener('dblclick', e => this.onDblClick(e));
            b.addEventListener('click', e => this.emitCellClick(e));
            b.addEventListener('wheel', e => { this.bodyPanes[1].scrollTop += e.deltaY; e.preventDefault(); }, { passive: false });
        }
        this.footInner = this.footPanes[1].firstElementChild;
        this.ime = r.querySelector('.og-ime');
        this.drop = r.querySelector('.og-drop');
        this.cal = r.querySelector('.og-cal');
        this.cal.addEventListener('mousedown', e => {
            e.preventDefault(); // 포커스는 ime 에
            const b = e.target.closest('button');
            if (!b || !this.editing || b.getAttribute('aria-disabled') === 'true')
                return;
            this.onCalButton(b);
        });
        // 셀 툴팁: 마우스가 올라갈 때만 계산
        for (const p of this.bodyPanes)
            p.addEventListener('mouseover', e => this.cellTooltip(e));
        this.tip = r.querySelector('.og-tip');
        this.errPanel = document.createElement('div');
        this.errPanel.className = 'og-errp';
        this.errPanel.hidden = true;
        this.errPanel.setAttribute('role', 'region');
        this.errPanel.setAttribute('aria-label', this.t('오류 목록'));
        r.appendChild(this.errPanel);
        this.dropLine = document.createElement('div');
        this.dropLine.className = 'og-dropline';
        this.dropLine.hidden = true;
        r.appendChild(this.dropLine);
        this.ghost = document.createElement('div');
        this.ghost.className = 'og-ghost';
        this.ghost.hidden = true;
        r.appendChild(this.ghost);
        r.setAttribute('role', 'grid');
        r.setAttribute('aria-multiselectable', 'true');
        if (!r.hasAttribute('aria-label'))
            r.setAttribute('aria-label', this.opts.ariaLabel ?? this.t('데이터 그리드'));
        this.live = document.createElement('div');
        this.live.className = 'og-live';
        this.live.setAttribute('aria-live', 'polite');
        this.live.setAttribute('aria-atomic', 'true');
        r.appendChild(this.live);
        const main = this.bodyPanes[1];
        main.addEventListener('scroll', () => { this.commitEditOnScroll(); this.render(false); }, { passive: true });
        for (const side of [this.bodyPanes[0], this.bodyPanes[2]])
            side.addEventListener('wheel', e => { main.scrollTop += e.deltaY; e.preventDefault(); }, { passive: false });
        for (const p of this.bodyPanes) {
            p.addEventListener('mousedown', e => this.onMouseDown(e));
            p.addEventListener('dblclick', e => this.onDblClick(e));
            p.addEventListener('click', e => this.emitCellClick(e));
        }
        r.addEventListener('mousemove', e => this.onMouseMove(e));
        window.addEventListener('mouseup', this.onMouseUp);
        this.head.addEventListener('click', e => this.onHeaderClick(e));
        this.head.setAttribute('role', 'row');
        this.head.setAttribute('aria-rowindex', '1');
        // 헤더 필터 단추: Tab 으로 가서 Enter·Space
        this.head.addEventListener('keydown', e => {
            const fb = e.target.closest('.og-fb');
            if (fb && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                const c = this.cols[+fb.dataset.ci];
                if (c)
                    this.openFilterMenu(c.id);
            }
        });
        this.head.addEventListener('dblclick', e => { const t = e.target; if (t.classList.contains('og-rs')) {
            const ci = +t.parentElement.dataset.ci;
            this.bestFit(this.cols[ci].id);
        } });
        this.head.addEventListener('contextmenu', e => this.onHeaderContext(e));
        for (const p of this.bodyPanes)
            p.addEventListener('contextmenu', e => { if (Date.now() < this.noCtxUntil) {
                e.preventDefault();
                return;
            } this.onCellContext(e); });
        // 터치: 탭은 브라우저가 만드는 마우스 이벤트(포커스)로, 두 번 탭·길게 누름은 여기서
        for (const p of this.bodyPanes) {
            p.addEventListener('touchstart', e => this.onTouchStart(e), { passive: false });
            p.addEventListener('touchmove', e => { const t = this.tch, q = e.touches[0]; if (t && q && Math.hypot(q.clientX - t.x, q.clientY - t.y) > 10) {
                t.moved = true;
                clearTimeout(t.timer);
            } }, { passive: true });
            p.addEventListener('touchend', e => this.onTouchEnd(e), { passive: false });
            p.addEventListener('touchcancel', () => { if (this.tch)
                clearTimeout(this.tch.timer); this.tch = null; }, { passive: true });
        }
        this.colMark = document.createElement('div');
        this.colMark.className = 'og-colmark';
        this.colMark.hidden = true;
        r.appendChild(this.colMark);
        this.gpanel.addEventListener('click', e => this.onGroupPanelClick(e));
        this.gpanel.addEventListener('dragstart', e => { const chip = e.target.closest('.og-gchip'); if (chip) {
            e.dataTransfer.setData('text/x-og-group', chip.dataset.col);
            e.dataTransfer.effectAllowed = 'move';
        } });
        this.gpanel.addEventListener('dragover', e => { if (e.dataTransfer?.types.includes('text/x-og-group'))
            e.preventDefault(); });
        this.gpanel.addEventListener('drop', e => {
            const col = e.dataTransfer?.getData('text/x-og-group');
            if (!col)
                return;
            e.preventDefault();
            const chips = [...this.gpanel.querySelectorAll('.og-gchip')];
            const at = chips.filter(c => c.getBoundingClientRect().left + c.offsetWidth / 2 < e.clientX && c.dataset.col !== col).length;
            this.addGroup(col, at);
        });
        this.gpanel.addEventListener('dragend', e => {
            const chip = e.target.closest('.og-gchip');
            const b = this.gpanel.getBoundingClientRect();
            if (chip && e.dataTransfer?.dropEffect === 'none' && (e.clientY < b.top - 10 || e.clientY > b.bottom + 10))
                this.removeGroup(chip.dataset.col); // 패널 밖으로 끌어내면 그룹 해제
        });
        this.head.addEventListener('mousedown', e => this.onHeaderMouseDown(e));
        // 터치: 컬럼 너비 손잡이 끌기
        this.head.addEventListener('touchstart', e => { const t = e.target; if (e.touches.length === 1 && t.classList.contains('og-rs'))
            this.touchDrag(e, t, false); }, { passive: false });
        this.head.addEventListener('input', e => this.onFilterInput(e));
        const ime = this.ime;
        // 1.2.0: 숨은 그리드(display:none — 탭 keepAlive 등)는 키·붙여넣기·복사에 반응하지 않음 (포커스가 남아 있어도 놓아 줌)
        ime.addEventListener('keydown', e => { if (this.hiddenInput(e))
            return; this.onKeyDown(e); });
        ime.addEventListener('compositionstart', () => this.onCompositionStart());
        ime.addEventListener('compositionend', () => this.onCompositionEnd());
        ime.addEventListener('input', () => this.onImeInput());
        ime.addEventListener('wheel', e => { if (this.editing?.kind === 'spin') {
            e.preventDefault();
            this.spinBy(e.deltaY < 0 ? 1 : -1, e.shiftKey);
        } }, { passive: false });
        ime.addEventListener('copy', e => { if (!this.hiddenInput(null))
            this.onCopy(e); });
        ime.addEventListener('cut', e => { if (!this.hiddenInput(e))
            this.onCut(e); });
        ime.addEventListener('paste', e => { if (!this.hiddenInput(e))
            this.onPaste(e); });
        ime.addEventListener('blur', () => { this.root.classList.remove('og-active'); });
        ime.addEventListener('focus', () => { this.root.classList.add('og-active'); });
        this.drop.addEventListener('mousedown', e => {
            e.preventDefault();
            if (this.editing?.kind === 'checkCombo') {
                this.onCheckComboMouse(e);
                return;
            } // 체크 콤보: 줄 = 켜기·끄기 (확정은 Enter)
            const hs = e.target.closest('.og-dlh span');
            if (hs?.dataset.f) {
                this.sortDrop(hs.dataset.f);
                return;
            } // 다중 컬럼 목록 머리글 = 그 칸으로 정렬 (ExtLookup 처럼)
            const it = e.target.closest('.og-di');
            if (it) {
                this.dropIdx = +it.dataset.i;
                this.commitEdit();
            }
        });
    }
    // ═══════════════════════ 본문 렌더 ═══════════════════════
    /** @internal */ bump() { this.ver++; this.requestRender(false); this.queueFocusEvents(); }
    /** @internal */ queueFocusEvents() {
        if (this.evQueued || !(this.opts.onCellFocus || this.opts.onSelectionChange || this.cellFocusListeners.size || this.selListeners.size))
            return;
        this.evQueued = true;
        queueMicrotask(() => { this.evQueued = false; if (this.root.isConnected)
            this.fireFocusEvents(); });
    }
    /** @internal */ fireFocusEvents() {
        const nir = this.focus.vi === NIR, row = this.getFocusedRow(), col = this.cols[this.focus.ci]?.id ?? null;
        const key = (nir ? 'n' : row >= 0 ? 'r' + row : 'g' + this.focus.vi) + '|' + col;
        if (key !== this.lastCellKey) {
            const prev = this.lastCell;
            this.lastCellKey = key;
            this.lastCell = { row, col };
            const e = { row, col: col ?? '', prevRow: prev.row, prevCol: prev.col, newItemRow: nir, grid: this };
            try {
                this.opts.onCellFocus?.(e);
            }
            catch (err) {
                console.error(err);
            }
            for (const f of this.cellFocusListeners) {
                try {
                    f(e);
                }
                catch (err) {
                    console.error(err);
                }
            }
        }
        const rs = this.selRanges(), sig = rs.map(r => `${r.r0},${r.r1},${r.c0},${r.c1}`).join(';') + '|' + (nir ? 'n' : '');
        if (sig !== this.lastSelSig) {
            this.lastSelSig = sig;
            const e = { ranges: nir ? [] : this.getSelectedRanges().map(r => ({ r0: r.r0, r1: r.r1, c0: r.c0, c1: r.c1, rows: r.rows, cols: r.cols })), grid: this };
            try {
                this.opts.onSelectionChange?.(e);
            }
            catch (err) {
                console.error(err);
            }
            for (const f of this.selListeners) {
                try {
                    f(e);
                }
                catch (err) {
                    console.error(err);
                }
            }
        }
    }
    /** @internal */ requestRender(full) {
        if (full)
            this.ver++;
        if (this.rafId)
            return;
        this.rafId = requestAnimationFrame(() => {
            this.rafId = 0;
            if (this.ro && !this.isShown()) {
                this.ver++;
                return;
            } // 숨은 그리드: 보일 때 ResizeObserver 가 다시 부름
            if (this.opts.showFooter)
                this.renderFooter();
            this.render(true);
            if (this.opts.selectionSummary)
                this.updateSelSum();
        });
    }
    /** 화면에 보이나 (문서에 붙어 있고 display:none 조상이 없음) */
    isShown() { return this.root.isConnected && this.root.getClientRects().length > 0; }
    /** @internal */ hiddenInput(e) {
        if (this.isShown())
            return false;
        e?.preventDefault();
        if (document.activeElement === this.ime)
            this.ime.blur();
        return true;
    }
    /** @internal */ onHidden() {
        if (this.menuEl || this.popEl || this.builderEl) {
            this.closeFilterMenu();
            this.closePopup();
            this.closeFilterBuilder();
        }
        if (!this.tip.hidden)
            this.hideTip();
    }
    /** @internal */ render(force) {
        if (force)
            this.refitIfResized();
        this.updateHeaderChecks();
        const main = this.bodyPanes[1];
        const rh = this.rh, vh = main.clientHeight, vw = main.clientWidth;
        const n = this.disp.length;
        if (this.offs && this.offsVer !== this.ds.version) { // 편집으로 내용이 바뀌면 바뀐 행만 다시 재고 (못 하면 전체)
            if (!this.updateOffsets()) {
                if (!this.htDirty?.size)
                    this.htInvalidate();
                this.buildOffsets();
            }
            const h = Math.min(this.totalH(), MAX_SCROLL_PX) + 'px';
            if (this.sizer.style.height !== h)
                this.sizer.style.height = h;
        }
        const total = this.totalH();
        const scaled = total > MAX_SCROLL_PX;
        const st = main.scrollTop, sl = main.scrollLeft;
        const vTop = scaled ? (st / Math.max(1, MAX_SCROLL_PX - vh)) * (total - vh) : st;
        const first = Math.max(0, this.viAt(vTop) - 2);
        const count = Math.ceil(vh / (this.offs && this.opts.rowLayout ? this.lineH : rh)) + 5; // 멀티로우 그룹 머리행은 한 줄 높이
        this.first = first;
        if (this.srm && !this.srvG)
            this.srm.ensure(first, first + count);
        const yOff = this.rowTop(first) - vTop;
        this.layers[1].style.transform = `translate3d(0,${st + yOff}px,0)`;
        this.layers[0].style.transform = this.layers[2].style.transform = `translate3d(0,${yOff}px,0)`;
        this.headInner.style.transform = this.footInner.style.transform = `translate3d(${-sl}px,0,0)`;
        // 보이는 스크롤 영역 컬럼
        let c0 = this.cols.findIndex(c => c.pane === 1);
        if (c0 < 0)
            c0 = this.cols.length;
        let c1 = this.cols.length - 1;
        if (!this.opts.rowLayout) { // 멀티로우는 left 가 단조가 아니므로 전부 그림 (컬럼 수 적음)
            while (c0 < c1 && this.cols[c0].left + this.cols[c0].width < sl)
                c0++;
            while (c1 > c0 && this.cols[c1].left > sl + vw)
                c1--;
        }
        while (this.pool.length < count)
            this.pool.push(this.makeRow(this.pool.length));
        const rng = this.range();
        for (let s = 0; s < this.pool.length; s++) {
            const p = this.pool[s], vi = first + s;
            if (s >= count || vi >= n) {
                if (p.key !== 'x') {
                    p.els.forEach(e => (e.hidden = true));
                    p.key = 'x';
                }
                continue;
            }
            const key = `${vi}|${this.disp[vi]}|${c0}|${c1}|${this.ver}`;
            if (p.key === key)
                continue;
            p.key = key;
            p.els.forEach(e => (e.hidden = false));
            if (this.offs) { // 가변 높이: 슬롯 위치·높이를 행마다
                const t = this.rowTop(vi) - this.rowTop(first) + 'px', h = this.rowH(vi) + 'px';
                for (const e of p.els) {
                    e.style.top = t;
                    e.style.height = h;
                }
                p.var = true;
            }
            else if (p.var) {
                for (const e of p.els) {
                    e.style.top = s * rh + 'px';
                    e.style.height = rh + 'px';
                }
                p.var = false;
            }
            this.fillRow(p, vi, c0, c1, rng, sl);
        }
        if (this.opts.detail || this.form?.mode === 'inline')
            this.placeDetails(first, Math.min(n, first + count), vTop, vh);
        if (this.nirOpt())
            this.renderNir(sl, rng);
        else if (!this.pinBox[3].hidden)
            this.pinBox[3].hidden = true;
        if (this.opts.pinnedRows || this.pinList[0].length + this.pinList[1].length || this.pinPool[0].length + this.pinPool[1].length)
            this.renderPinned(sl, rng);
        if (this.opts.stickyGroupRows || this.pinPool[2].length)
            this.renderSticky(vTop, sl, rng);
        this.placeFillHandle(vTop, sl);
        if (this.rowEdit || this.rowEditBar)
            this.placeRowEditBar(vTop);
        if (this.mergedEls.size)
            this.centerMerged(vTop, vh);
        this.positionIme();
    }
    /** 상·하단 고정 행: disp 의 처음/마지막 데이터 행 N 개를 복사해 그림. 클릭하면 실제 행으로 포커스 */
    /** @internal */ renderPinned(sl, rng) {
        const pr = this.opts.pinnedRows ?? {};
        const m0 = this.cols.findIndex(c => c.pane === 1);
        for (const side of [0, 1]) {
            const want = Math.max(0, (side ? pr.bottom : pr.top) ?? 0) | 0, vis = [];
            if (want) {
                if (side) {
                    for (let i = this.disp.length - 1; i >= 0 && vis.length < want; i--)
                        if (this.disp[i] >= 0)
                            vis.unshift(i);
                }
                else
                    for (let i = 0; i < this.disp.length && vis.length < want; i++)
                        if (this.disp[i] >= 0)
                            vis.push(i);
            }
            // pinRow 로 고정한 행 (보이는 행만, 고정한 순서대로)
            for (const r of this.pinList[side]) {
                const vi = this.disp.indexOf(r);
                if (vi >= 0 && !vis.includes(vi))
                    vis.push(vi);
            }
            this.fillPinBox(side, vis, sl, rng, m0 < 0 ? this.cols.length : m0, 0);
        }
    }
    /** 고정 상자(0 상단·1 하단·2 그룹 고정)에 disp 의 vis 행들을 복사해 그림 */
    /** @internal */ fillPinBox(side, vis, sl, rng, c0, gsl) {
        const box = this.pinBox[side], panes = this.pinPanes[side], pool = this.pinPool[side];
        box.hidden = !vis.length;
        if (!vis.length)
            return;
        box.style.height = vis.length * this.rh + 'px';
        panes[1].firstElementChild.style.transform = `translate3d(${-sl}px,0,0)`;
        while (pool.length < vis.length) {
            const slot = pool.length;
            const mk = (pane) => { const el = document.createElement('div'); el.className = 'og-r'; el.style.top = slot * this.rh + 'px'; el.style.height = this.rh + 'px'; el.__slot = slot; (pane === 1 ? panes[1].firstElementChild : panes[pane]).appendChild(el); return el; };
            pool.push({ els: [mk(0), mk(1), mk(2)], cells: [[], [], []], slot, key: '' });
        }
        pool.forEach((p, i) => {
            if (i >= vis.length) {
                p.els.forEach(e => (e.hidden = true));
                p.key = 'x';
                return;
            }
            const vi = vis[i];
            p.els.forEach(e => { e.hidden = false; e.__pinVi = vi; });
            const key = `${vi}|${this.disp[vi]}|${this.ver}|${gsl}`;
            if (p.key === key)
                return;
            p.key = key;
            this.fillRow(p, vi, c0, this.cols.length - 1, rng, gsl);
        });
    }
    /** 고정 그룹: vi 행의 상위 그룹 머리행 vi 목록 (바깥 단계부터). vi 가 그룹 머리행이면 그보다 위 단계만 */
    stickyChain(vi) {
        const d = this.disp, n = d.length, out = [];
        if (!this.isGrouped() || vi < 0 || vi >= n)
            return out;
        if (this.sgDisp !== d) { // disp 가 바뀌면 다시 계산 (O(n))
            const head = new Int32Array(n), par = new Int32Array(n), st = [];
            let last = -1;
            for (let i = 0; i < n; i++) {
                const v = d[i];
                if (v < 0 && v > EXTRA) {
                    const L = this.groups[-v - 1].level;
                    while (st.length && this.groups[-d[st[st.length - 1]] - 1].level >= L)
                        st.pop();
                    par[i] = st.length ? st[st.length - 1] : -1;
                    st.push(i);
                    last = i;
                }
                else
                    par[i] = -1;
                head[i] = last;
            }
            this.sgHead = head;
            this.sgParent = par;
            this.sgDisp = d;
        }
        const isG = (i) => d[i] < 0 && d[i] > EXTRA;
        let h = isG(vi) ? this.sgParent[vi] : this.sgHead[vi];
        while (h >= 0) {
            out.unshift(h);
            h = this.sgParent[h];
        }
        return out;
    }
    /** @internal */ renderSticky(vTop, sl, rng) {
        let vis = [];
        if (this.opts.stickyGroupRows && vTop > 0) {
            const top = this.viAt(vTop), n = this.disp.length;
            for (let k = 0; k < 4; k++) { // 고정 줄 수만큼 아래 행 기준으로 다시 (수렴)
                const ch = this.stickyChain(Math.min(n - 1, top + vis.length));
                if (ch.length === vis.length && ch.every((v, i) => v === vis[i]))
                    break;
                vis = ch;
            }
        }
        const main = this.bodyPanes[1], panes = this.pinPanes[2];
        panes[1].style.right = this.paneWidth[2] + main.offsetWidth - main.clientWidth + 'px';
        const m0 = this.cols.findIndex(c => c.pane === 1);
        this.fillPinBox(2, vis, sl, rng, m0 < 0 ? this.cols.length : m0, sl);
    }
    /** @internal */ makeRow(slot) {
        const mk = (pane) => {
            const el = document.createElement('div');
            el.className = 'og-r';
            el.setAttribute('role', 'row');
            el.style.top = slot * this.rh + 'px';
            el.style.height = this.rh + 'px';
            el.__slot = slot;
            this.layers[pane].appendChild(el);
            return el;
        };
        return { els: [mk(0), mk(1), mk(2)], cells: [[], [], []], slot, key: '' };
    }
    /** @internal */ cellEl(p, pane, k) {
        const arr = p.cells[pane];
        while (arr.length <= k) {
            const el = document.createElement('div');
            el.className = 'og-c';
            el.setAttribute('role', 'gridcell');
            const text = document.createTextNode('');
            el.appendChild(text);
            p.els[pane].appendChild(el);
            arr.push({ el, text, ci: -1, key: '' });
        }
        return arr[k];
    }
    /** @internal */ fillRow(p, vi, c0, c1, rng, sl) {
        const item = vi === NIR ? this.nirRow : this.disp[vi];
        if (item <= EXTRA) {
            this.fillExtraRow(p, vi, item, c0, c1, sl);
            return;
        }
        const isGroup = item < 0;
        const focusRow = vi === this.focus.vi;
        const st = isGroup ? 0 : this.ds.rowState(item);
        const rowCls = 'og-r' + (vi & 1 ? ' og-odd' : '') + (focusRow ? ' og-fr' : '') + (st === RowState.INSERT ? ' og-ri' : st === RowState.UPDATE ? ' og-ru' : st === RowState.DELETE ? ' og-rd' : '') + (this.rowEdit && this.rowEdit.row === item && !isGroup ? ' og-redit' : '') + (!isGroup && this.checked.has(item) ? ' og-rc' : '') + (isGroup ? ' og-rg' : '') + (this.srm && !this.srvG && !isGroup && !this.srm.isLoaded(item) ? ' og-ld' : '');
        p.els[0].className = p.els[1].className = p.els[2].className = isGroup ? rowCls : rowCls + this.rowClassOf(item) + (this.srvChanged.size && this.srvChanged.has(item) ? ' og-srvchg' : '');
        if (p.ari !== vi && vi >= 0) {
            p.ari = vi;
            const ri = String(vi + 2);
            for (const e of p.els)
                e.setAttribute('aria-rowindex', ri);
        } // 1 = 헤더 줄
        if (isGroup) {
            this.fillGroupRow(p, this.groups[-item - 1], c0, c1, sl);
            return;
        }
        if (p.group) {
            p.group.remove();
            p.group = undefined;
        }
        // 좌·우 고정 영역은 전부, 스크롤 영역은 보이는 범위만. colSpan 이면 뒤 칸을 가린다
        const run = (pane, from, to) => {
            let k = 0, skip = 0;
            for (let ci = from; ci <= to; ci++, k++) {
                const c = this.cols[ci];
                if (!c || c.pane !== pane)
                    break;
                const ce = this.cellEl(p, pane, k);
                if (skip > 0) {
                    skip--;
                    ce.el.hidden = true;
                    ce.ci = -1;
                    continue;
                }
                this.fillCell(ce, c, item, vi, rng);
                const span = c.def.colSpan ? Math.max(1, c.def.colSpan(item, this.ds) | 0) : 1;
                if (span > 1) {
                    let w = 0;
                    for (let j = ci; j < ci + span && j < this.cols.length && this.cols[j].pane === pane; j++)
                        w += this.cols[j].width;
                    ce.el.style.width = w + 'px';
                    ce.wide = true;
                    skip = span - 1;
                }
                else if (ce.wide) {
                    ce.el.style.width = c.width + 'px';
                    ce.wide = false;
                }
            }
            for (; k < p.cells[pane].length; k++)
                p.cells[pane][k].el.hidden = true;
        };
        const firstOf = (pane) => this.cols.findIndex(c => c.pane === pane);
        const l0 = firstOf(0), r0 = firstOf(2);
        if (l0 >= 0)
            run(0, l0, this.cols.length - 1);
        else
            for (const x of p.cells[0])
                x.el.hidden = true;
        run(1, c0, c1);
        if (r0 >= 0)
            run(2, r0, this.cols.length - 1);
        else
            for (const x of p.cells[2])
                x.el.hidden = true;
    }
    /** 추가 슬롯 (디테일 자리) */
    /** @internal */ fillExtraRow(p, _vi, item, c0, c1, _sl) {
        const x = this.extras[EXTRA - item];
        p.els[0].className = p.els[1].className = p.els[2].className = x?.kind === 'gfoot' ? 'og-r og-rgf' : x?.kind === 'sub' ? 'og-r og-rgf og-rsub' : 'og-r og-rx';
        if (p.group) {
            p.group.remove();
            p.group = undefined;
        }
        for (const arr of p.cells)
            for (const c of arr)
                c.el.hidden = true;
        if (x?.kind === 'sub') { // 밖에서 준 소계 줄 (Nexacro ROWTYPE_GROUP)
            const k = [0, 0, 0];
            for (let ci = 0; ci < this.cols.length; ci++) {
                const c = this.cols[ci];
                if (c.special || (c.pane === 1 && (ci < c0 || ci > c1)))
                    continue;
                const v = this.extOrder?.sub?.(x.k, c.id);
                if (v == null || v === '')
                    continue;
                const ce = this.cellEl(p, c.pane, k[c.pane]++);
                ce.el.hidden = false;
                ce.ci = -1;
                if (ce.sty)
                    this.applyCellStyle(ce, null);
                if (ce.htmlStr) {
                    ce.html.innerHTML = '';
                    ce.htmlStr = '';
                }
                ;
                if (ce.cust)
                    this.dropCust(ce);
                if (ce.el.style.height)
                    ce.el.style.top = ce.el.style.height = '';
                ce.el.className = 'og-c og-gs' + (typeof v === 'number' ? ' og-num' : '');
                ce.el.style.left = c.left + 'px';
                ce.el.style.width = c.width + 'px';
                ce.text.nodeValue = typeof v === 'number' ? this.displayText(c, v, -1) : String(v);
            }
            return;
        }
        if (x?.kind !== 'gfoot' || !x.g)
            return;
        // 그룹 푸터: 집계 컬럼 아래에 그 그룹 값, 첫 일반 컬럼에 "라벨 소계"
        const g = x.g, k = [0, 0, 0];
        const labelCi = this.cols.findIndex(c => !c.special && !colAggs(c.def).length && !c.def.summaryExpr && !isCustomSummary(c.def.summary));
        for (let ci = 0; ci < this.cols.length; ci++) {
            const c = this.cols[ci];
            if (c.pane === 1 && (ci < c0 || ci > c1) && ci !== labelCi)
                continue;
            const aggs = colAggs(c.def), isLabel = ci === labelCi;
            if (!isLabel && !aggs.length && !c.def.summaryExpr && !isCustomSummary(c.def.summary))
                continue;
            const ce = this.cellEl(p, c.pane, k[c.pane]++);
            ce.el.hidden = false;
            ce.ci = -1;
            if (ce.sty)
                this.applyCellStyle(ce, null);
            if (ce.htmlStr) {
                ce.html.innerHTML = '';
                ce.htmlStr = '';
            }
            ;
            if (ce.cust)
                this.dropCust(ce);
            if (ce.el.style.height)
                ce.el.style.top = ce.el.style.height = ''; // 멀티로우 셀 자리로 쓰던 요소
            ce.el.className = isLabel ? 'og-c og-gfl' : 'og-c og-num og-gs';
            ce.el.style.left = c.left + 'px';
            ce.el.style.width = (isLabel ? Math.max(c.width, 160) : c.width) + 'px';
            ce.text.nodeValue = isLabel ? `${'\u3000'.repeat(g.level)}${g.label} 소계` : aggs.length ? this.aggText(c, g.rows, aggs[0]) : this.summaryText(c, g.rows);
        }
    }
    /** 그룹 머리행 글자 (groupRowText 또는 "컬럼: 값 (건수)") */
    /** @internal */ groupText(g) {
        const header = g.col ? this.headerOf(g.col) : '';
        const n = g.count ?? (this.opts.showDeletedRows ? this.liveRows(g.rows).length : g.rows.length); // 삭제 행 표시 중이어도 건수는 삭제 행 빼고
        return this.opts.groupRowText ? this.opts.groupRowText({ col: g.col ?? '', header, label: g.label, count: n, level: g.level }) : `${header ? header + ': ' : ''}${g.label} (${n.toLocaleString('ko-KR')})${g.node?.loading ? ' …' : ''}`;
    }
    /** @internal */ fillGroupRow(p, g, c0, c1, sl) {
        for (const arr of p.cells)
            for (const c of arr)
                c.el.hidden = true;
        // 왼쪽 고정 구역이 제목을 담을 만큼 넓을 때만 거기에 (행 번호·상태 칸뿐이면 "▾ 진료"처럼 잘림 → 본문에, 스크롤 위치에 맞춰)
        const labelPane = this.paneWidth[0] >= GROUP_LABEL_MIN_PANE ? 0 : 1;
        if (!p.group) {
            p.group = document.createElement('div');
            p.group.className = 'og-gl';
        }
        p.els[labelPane].appendChild(p.group);
        p.group.style.left = (labelPane === 1 ? sl : 0) + 'px';
        p.group.style.paddingLeft = 8 + g.level * 18 + 'px';
        p.group.textContent = `${g.collapsed ? '▸' : '▾'} ${this.groupText(g)}`;
        // 그룹 소계
        let k = 0;
        for (let ci = c0; ci <= c1 && ci < this.cols.length; ci++) {
            const c = this.cols[ci];
            const aggs = colAggs(c.def);
            if (!aggs.length && !c.def.summary && !c.def.summaryExpr)
                continue;
            if (c.lh && c.pane !== 1)
                continue;
            const ce = this.cellEl(p, 1, k++);
            ce.el.hidden = false;
            if (ce.sty)
                this.applyCellStyle(ce, null);
            if (ce.htmlStr) {
                ce.html.innerHTML = '';
                ce.htmlStr = '';
            }
            ;
            if (ce.cust)
                this.dropCust(ce);
            if (ce.el.style.height)
                ce.el.style.top = ce.el.style.height = ''; // 멀티로우 셀 자리로 쓰던 요소
            ce.el.className = 'og-c og-num og-gs';
            ce.el.style.left = c.left + 'px';
            ce.el.style.width = c.width + 'px';
            ce.text.nodeValue = aggs.length ? this.aggText(c, g.rows, aggs[0]) : this.summaryText(c, g.rows);
            ce.ci = -1;
        }
    }
    /** @internal */ fillCell(ce, c, row, vi, rng) {
        const el = ce.el;
        if (el.hidden)
            el.hidden = false;
        if (ce.ci !== c.ci) {
            el.style.left = c.left + 'px';
            el.style.width = c.width + 'px';
            ce.ci = c.ci;
            el.__ci = c.ci;
            if (c.lh) {
                el.style.top = c.lt * this.lineH + 'px';
                el.style.height = c.lh * this.lineH + 'px';
            }
            else if (el.style.height)
                el.style.top = el.style.height = '';
        }
        let text = '', cls = 'og-c', ro = true, lvl = '';
        if (c.special && ce.sty)
            this.applyCellStyle(ce, null);
        if (c.special && ce.htmlStr) {
            ce.html.innerHTML = '';
            ce.htmlStr = '';
        }
        if (c.special && ce.cust)
            this.dropCust(ce);
        if (c.special === 'indicator') {
            const ind = vi === NIR ? { sym: '✱', cls: 'og-ind-new', title: this.t('새 항목 행') } : this.indicatorOf(row, vi);
            text = ind.sym;
            cls += ' og-sp og-ind' + (ind.cls ? ' ' + ind.cls : '');
            if (el.title !== ind.title)
                el.title = ind.title;
        }
        else if (c.special === 'state') {
            const s = this.ds.rowState(row);
            text = s === RowState.INSERT ? '+' : s === RowState.UPDATE ? '✎' : s === RowState.DELETE ? 'D' : '';
            cls += ' og-sp og-st' + (s === RowState.INSERT ? ' og-st-i' : s === RowState.UPDATE ? ' og-st-u' : s === RowState.DELETE ? ' og-st-d' : '');
        }
        else if (c.special === 'rownum') {
            text = vi === NIR ? '*' : this.isGrouped() ? '' : String((this.rowNo ? this.rowNo[vi] : vi) + 1);
            cls += ' og-sp og-rn';
        }
        else if (c.special === 'detail') {
            text = this.detailOpen.has(row) ? '▾' : '▸';
            cls += ' og-sp og-dt';
        }
        else if (c.special === 'drag') {
            text = '⋮⋮';
            cls += ' og-sp og-dh';
        }
        else if (c.special === 'check') {
            cls += ' og-sp og-ck' + (this.checked.has(row) ? ' og-on' : this.treeMixed.has(row) ? ' og-mixed' : '');
        }
        else {
            const v = this.valueOf(c, row);
            text = this.displayText(c, v, row);
            const d = c.def;
            const kind = this.editorOf(c, row);
            if (kind && CHECKISH.has(kind)) {
                cls += ' og-ck' + (kind === 'radio' ? ' og-radio' : '') + (this.isChecked(c, v) ? ' og-on' : kind === 'check3' && v === this.checkValues3(c)[2] ? ' og-mixed' : '');
                text = '';
            }
            cls += d.align === 'center' ? ' og-ctr' : d.align === 'right' || (d.align == null && (this.ds.cols[c.id]?.kind === 'num' || typeof v === 'number')) ? ' og-num' : '';
            if (this.opts.editable && kind && this.canEditCol(c, row)) {
                cls += ' og-ed';
                ro = false;
                if (kind === 'button')
                    cls += ' og-btned';
            }
            if (d.wrap)
                cls += ' og-wrap';
            if (vi === NIR) { /* 새 항목 행: 병합·가림 없음 */ }
            else if (this.mergeActive(c) && !this.opts.tree) {
                const prev = vi > 0 ? this.disp[vi - 1] : -1;
                if (vi !== this.first && prev >= 0 && this.sameMerge(c, prev, row)) {
                    cls += ' og-mcov';
                    text = '';
                }
                else {
                    let e = vi;
                    const lim = Math.min(this.disp.length - 1, this.first + this.pool.length - 1);
                    while (e < lim && this.disp[e + 1] >= 0 && this.sameMerge(c, this.disp[e], this.disp[e + 1]))
                        e++;
                    if (e > vi) {
                        el.style.height = this.rowTop(e + 1) - this.rowTop(vi) + 'px';
                        cls += ' og-merged';
                        ce.m0 = vi;
                        ce.m1 = e;
                        this.mergedEls.add(ce);
                    }
                }
            }
            else if (c.lh && !this.opts.tree && this.mergeOn(c)) { // 멀티로우 일부 줄 셀: 위 레코드와 같으면 가림
                const prev = vi > 0 ? this.disp[vi - 1] : -1;
                if (prev >= 0 && this.sameMerge(c, prev, row)) {
                    text = '';
                    cls += ' og-sup og-mcont';
                }
            }
            else if (d.rowSpan && !this.opts.tree) {
                // 행별 rowSpan: 위쪽 시작 행이 덮으면 가림, 시작 행(또는 화면 첫 행)이면 남은 만큼 늘림
                const span = (v) => { const r = this.disp[v]; return r >= 0 ? Math.max(1, Math.min(50, d.rowSpan(r, this.ds) | 0)) : 1; };
                let st = vi, en = vi;
                for (let cur = Math.max(0, vi - 50); cur <= vi;) {
                    const n = span(cur);
                    if (cur + n - 1 >= vi) {
                        st = cur;
                        en = cur + n - 1;
                        break;
                    }
                    cur += n;
                }
                if (st < vi && vi !== this.first) {
                    cls += ' og-mcov';
                    text = '';
                }
                else {
                    if (st < vi)
                        text = this.displayText(c, this.valueOf(c, this.disp[st]), this.disp[st]); // 화면 첫 행: 시작 행 값을 보여 줌
                    const lim = Math.min(this.disp.length - 1, this.first + this.pool.length - 1);
                    let e = vi;
                    while (e < Math.min(en, lim) && this.disp[e + 1] >= 0)
                        e++;
                    if (e > vi) {
                        el.style.height = this.rowTop(e + 1) - this.rowTop(vi) + 'px';
                        cls += ' og-merged';
                        ce.m0 = vi;
                        ce.m1 = e;
                        this.mergedEls.add(ce);
                    }
                }
            }
            else if (d.suppress && vi > 0 && this.isSuppressed(c, row, vi)) {
                text = '';
                cls += ' og-sup';
            }
            if (!cls.includes(' og-merged')) {
                if (c.lh) {
                    const h = c.lh * this.lineH + 'px';
                    if (el.style.height !== h)
                        el.style.height = h;
                }
                else if (el.style.height)
                    el.style.height = '';
            }
            if (!cls.includes(' og-merged') && ce.m0 != null)
                this.unmerge(ce);
            if (this.ds.isCellChanged(row, c.id) || (this.rowEdit && this.rowEdit.row === row && this.rowEdit.vals.has(c.id)))
                cls += ' og-chg';
            if (this.errors.has(row + ':' + c.id) || this.asyncErr.has(row + ':' + c.id) || (this.rowErrs.size && this.rowErrs.has(row + ':' + c.id))) {
                cls += ' og-err';
                lvl = 'error';
            }
            else if (this.warns.size || this.asyncWarn.size || this.rowWarns.size) {
                const w = this.cellWarn(row, c.id);
                if (w) {
                    cls += w.level === 'warning' ? ' og-warn' : ' og-info';
                    lvl = w.level;
                }
            }
            if (this.asyncPend.size && this.asyncPend.has(row + ':' + c.id))
                cls += ' og-pend';
            if (this.ds.notes.size && this.ds.getNote(row, c.id))
                cls += ' og-note';
            if (this.flashUntil.size && (this.flashUntil.get(row + ':' + c.id) ?? 0) > performance.now())
                cls += ' og-flash';
            if (d.cellClass) {
                const x = d.cellClass(v, row, this.ds);
                if (x)
                    cls += ' ' + x;
            }
            if (d.rules) {
                const rr = evalRules(d.rules, v, row, this.ds, r => this.ruleRange(c, r));
                cls += rr.cls;
                if (rr.prefix && text)
                    text = rr.prefix + text;
                const st = { ...rr.style };
                if (rr.bg)
                    st.background = rr.bg;
                this.applyCellStyle(ce, st);
            }
            else if (ce.sty)
                this.applyCellStyle(ce, null);
            if ((d.render || (d.cellRenderer && this.opts.renderCell)) && !cls.includes(' og-mcov') && !cls.includes(' og-sup')) {
                // 사용자 렌더러: 셀 안 전용 요소에. 값·행·데이터 버전이 같으면 다시 부르지 않음 (스크롤만으로는 안 부름)
                if (!ce.cust) {
                    ce.cust = document.createElement('span');
                    ce.cust.className = 'og-cr';
                    el.appendChild(ce.cust);
                }
                if (ce.cust.hidden)
                    ce.cust.hidden = false;
                const sig = c.ci + '|' + row + '|' + this.ds.version + '|' + typeof v + '|' + v;
                if (ce.custSig !== sig) {
                    this.dropCust(ce, false);
                    ce.custSig = sig;
                    const ctx = { value: v, text, row, col: c.id, ds: this.ds, grid: this, column: d };
                    try {
                        ce.custClean = d.render ? d.render(ce.cust, ctx) : this.opts.renderCell(ce.cust, ctx);
                    }
                    catch (err) {
                        console.error(err);
                    }
                    this.custCells.add(ce);
                }
                text = '';
                cls += ' og-crc';
            }
            else if (ce.cust)
                this.dropCust(ce);
            if (d.cellType) {
                const h = cellHtml(d.cellType, v, text, { badgeColors: d.badgeColors, header: d.header, width: c.width });
                if (!ce.html) {
                    ce.html = document.createElement('span');
                    ce.html.className = 'og-ct';
                    el.appendChild(ce.html);
                }
                if (ce.htmlStr !== h) {
                    setHtml(ce.html, h);
                    ce.htmlStr = h;
                }
                text = '';
                cls += ' og-ct-' + d.cellType;
            }
            else if (ce.htmlStr) {
                ce.html.innerHTML = '';
                ce.htmlStr = '';
            }
        }
        if (this.opts.tree && !c.special && c.id === this.treeLabelCol()) {
            const lv = this.treeLevel.get(row) ?? 0;
            const kids = this.treeKids.has(row);
            const open = kids && !this.treeCollapsed.has(String(this.ds.get(row, this.opts.tree.idCol)));
            text = (kids ? (this.treeLoading.has(String(this.ds.get(row, this.opts.tree.idCol))) ? '⋯ ' : open ? '▾ ' : '▸ ') : '• ') + text;
            cls += ' og-tree' + (kids ? ' og-tk' : '');
            const pl = 6 + lv * 16 + 'px';
            if (el.style.paddingLeft !== pl)
                el.style.paddingLeft = pl;
        }
        else if (el.style.paddingLeft)
            el.style.paddingLeft = '';
        if (this.hits.size && !c.special && this.hits.has(row + ':' + c.id)) {
            const cur = this.findMatches[this.findIdx];
            cls += cur && cur.row === row && cur.col === c.id ? ' og-hit og-hitcur' : ' og-hit';
        }
        let selA = false;
        if (vi === this.focus.vi && c.ci === this.focus.ci) {
            cls += ' og-focus';
            selA = true;
        }
        else if ((vi >= rng.r0 && vi <= rng.r1 && c.ci >= rng.c0 && c.ci <= rng.c1 && (rng.r0 !== rng.r1 || rng.c0 !== rng.c1)) || (this.extraRanges.length && this.inExtra(vi, c.ci))) {
            cls += ' og-sel';
            selA = true;
        }
        if (el.className !== cls)
            el.className = cls;
        // 접근성: 컬럼 위치·선택 (값이 바뀔 때만 DOM 갱신)
        if (ce.aci !== c.ci) {
            ce.aci = c.ci;
            el.setAttribute('aria-colindex', String(c.ci + 1));
        }
        if (ce.asel !== selA) {
            ce.asel = selA;
            el.setAttribute('aria-selected', String(selA));
        }
        // 1.2.0: 편집 불가 칸 aria-readonly, 오류·경고 칸 aria-invalid + aria-description(수준: 문구). 이미 계산한 값만 씀 — 바뀔 때만 DOM 갱신
        if (ce.aro !== ro) {
            ce.aro = ro;
            if (ro)
                el.setAttribute('aria-readonly', 'true');
            else
                el.removeAttribute('aria-readonly');
        }
        if (lvl || ce.alv)
            this.ariaLevel(ce, lvl, row, c.id);
        if (ce.text.nodeValue !== text)
            ce.text.nodeValue = text;
    }
    /** 사용자 렌더러 정리 (셀이 다른 내용으로 재사용될 때). hide=false 면 곧바로 다시 그릴 때 */
    /** @internal */ dropCust(ce, hide = true) {
        const f = ce.custClean;
        ce.custClean = undefined;
        ce.custSig = undefined;
        this.custCells.delete(ce);
        if (hide && ce.cust)
            ce.cust.hidden = true;
        if (typeof f === 'function') {
            try {
                f();
            }
            catch (err) {
                console.error(err);
            }
        }
    }
    /** 셀 인라인 스타일 (조건부 서식). 재사용 셀이므로 이전에 넣은 속성은 지움 */
    /** @internal */ applyCellStyle(ce, st) {
        const es = ce.el.style;
        if (ce.sty)
            for (const k of ce.sty)
                if (!st || !(k in st))
                    es[k] = '';
        if (st)
            for (const k in st)
                if (es[k] !== st[k])
                    es[k] = st[k];
        ce.sty = st && Object.keys(st).length ? Object.keys(st) : undefined;
    }
    /** @internal */ ruleRange(c, r) {
        const rr = r;
        if (rr.min != null && rr.max != null)
            return [rr.min, rr.max];
        if (this.ruleStatsView !== this.view) {
            this.ruleStats.clear();
            this.ruleStatsView = this.view;
        }
        let mm = this.ruleStats.get(c.id);
        if (!mm) {
            let lo = Infinity, hi = -Infinity;
            const v = this.view;
            for (let i = 0; i < v.length; i++) {
                const x = toNumber(this.valueOf(c, v[i]));
                if (x === x) {
                    if (x < lo)
                        lo = x;
                    if (x > hi)
                        hi = x;
                }
            }
            mm = lo <= hi ? [lo, hi] : [0, 0];
            this.ruleStats.set(c.id, mm);
        }
        return [rr.min ?? mm[0], rr.max ?? mm[1]];
    }
    /** 세로 병합 (멀티로우는 레코드 전체 높이 셀만 — 일부 줄 셀은 같은 값 가림으로) */
    /** @internal */ mergeActive(c) {
        if (c.lh && !(c.lt === 0 && c.lh >= this.lines))
            return false;
        return this.mergeOn(c);
    }
    /** @internal */ mergeOn(c) {
        const m = c.def.merge;
        if (!m)
            return false;
        if (m === 'sorted')
            return this.sortKeys.some(k => k.col === c.id) || this.groupCols().includes(c.id);
        return true;
    }
    /** 병합 판정: 사용자 규칙이 있으면 그것, 없으면 값이 같고 앞쪽 병합 컬럼들도 같을 때 */
    /** @internal */ sameMerge(c, a, b) {
        if (c.def.mergeWith)
            return c.def.mergeWith(a, b, this.ds);
        for (const x of this.cols) {
            if (x.special || !this.mergeOn(x))
                continue;
            const va = this.valueOf(x, a), vb = this.valueOf(x, b);
            if (va !== vb || va == null)
                return false;
            if (x === c)
                return true;
        }
        return false;
    }
    /** @internal */ isSuppressed(c, row, vi) {
        const prev = this.disp[vi - 1];
        if (prev == null || prev < 0)
            return false;
        for (const x of this.cols) {
            if (!x.def.suppress)
                continue;
            if (this.valueOf(x, row) !== this.valueOf(x, prev))
                return false;
            if (x === c)
                return true;
        }
        return false;
    }
    // ═══════════════════════ 값·표시 ═══════════════════════
    /** @internal */ valueOf(c, row) {
        const re = this.rowEdit;
        if (re && re.row === row)
            return c.def.value ? c.def.value(row, re.proxy) : re.vals.has(c.id) ? re.vals.get(c.id) : this.ds.get(row, c.id); // 행 편집 중: 버퍼 값
        return c.def.value ? c.def.value(row, this.ds) : this.ds.get(row, c.id);
    }
    /** 검증·편집 규칙에 줄 Dataset: 행 편집 중인 행이면 버퍼 값을 보여 주는 대리 */
    /** @internal */ dsFor(row) { const re = this.rowEdit; return re && re.row === row ? re.proxy : this.ds; }
    /** 헤더 필터 메뉴를 쓸 수 있는 컬럼: 일반 컬럼 + valueGetter 컬럼(클라이언트 행 모델에서만 — 서버는 계산 값을 모름) */
    /** @internal */ cellFilterable(ci) { const c = this.cols[ci]; return !!c && !c.special && (!c.def.value || this.menuFilterable(c)); }
    /** @internal */ menuFilterable(c) { return !c.special && c.def.filterable !== false && (!c.def.value || (!!c.def.valueGetter && !this.srm)); }
    /** @internal */ queryDs() {
        const gs = this.opts.columns.filter(c => c.valueGetter && c.value && !this.ds.cols[c.id]);
        if (!gs.length)
            return this.ds;
        const key = gs.map(c => c.id).join(','), q = this.qdsCache;
        if (q && q.ver === this.ds.version && q.sver === this.ds.structureVersion && q.key === key)
            return q.ds;
        const shim = Object.create(this.ds);
        const cols = { ...this.ds.cols };
        const n = this.ds.length;
        for (const c of gs) {
            const vals = new Array(n);
            let num = true;
            for (let r = 0; r < n; r++) {
                const v = c.value(r, this.ds);
                vals[r] = v;
                if (v != null && v !== '' && typeof v !== 'number')
                    num = false;
            }
            const dc = new Dataset([{ id: c.id, type: num ? 'number' : 'string' }], Math.max(16, n));
            dc.loadColumnar({ n, data: { [c.id]: vals } });
            cols[c.id] = dc.cols[c.id];
        }
        Object.defineProperty(shim, 'cols', { value: cols });
        this.qdsCache = { ver: this.ds.version, sver: this.ds.structureVersion, key, ds: shim };
        return shim;
    }
    displayText(c, v, row) {
        const d = c.def;
        if (v == null || v === '')
            return '';
        if (typeof d.format === 'function')
            return d.format(v, row, this.ds);
        const kind = this.editorOf(c, row);
        if (kind === 'checkCombo' || d.editor === 'checkCombo')
            return formatMulti(v, this.itemsOf(c, row), d.valueSeparator ?? ',', d.displaySeparator ?? ', ', d.comboShow);
        if (kind === 'combo' || d.items) {
            const it = this.comboMap(c, row).get(String(v));
            const lbl = it ? it.label : String(v);
            return d.comboShow === 'value' ? String(v) : d.comboShow === 'both' && it ? `${it.value} ${it.label}` : lbl;
        }
        if (d.privacy && !this.revealed.has(row + ':' + c.id))
            return maskPrivacy(String(v), d.privacy);
        if (typeof d.mask === 'string' && typeof v === 'string')
            return applyMask(v, d.mask);
        if (d.dateKind === 'datetime' && typeof v === 'string' && d.format !== 'date')
            return displayDateTime(v);
        if (d.editor === 'time' && typeof v === 'string' && /^\d{3,4}$/.test(v))
            return parseTime(v) ?? v;
        switch (d.format) {
            case 'number': return typeof v === 'number' ? formatNumber(v, d.decimals) : String(v);
            case 'money': return typeof v === 'number' ? formatNumber(v, d.decimals ?? 0) : String(v);
            case 'percent': return typeof v === 'number' ? formatNumber(v, d.decimals ?? 1) + '%' : String(v);
            case 'date': return displayDate(String(v));
            case 'datetime': return displayDateTime(String(v));
        }
        if (typeof v === 'number')
            return d.decimals != null ? formatNumber(v, d.decimals) : String(v);
        if (typeof v === 'boolean')
            return v ? 'Y' : 'N';
        if (this.ds.cols[c.id]?.type === 'date')
            return displayDate(String(v));
        return String(v);
    }
    /** @internal */ searchText(col, raw) {
        const c = this.cols.find(x => x.id === col);
        return c ? this.displayText(c, raw, -1) + ' ' + (raw ?? '') : String(raw ?? '');
    }
    /** 콤보 컬럼 필터에 명칭을 쳐도 코드로 찾게 */
    /** @internal */ comboFilterValue(c, text) {
        if (!c.def.items || /^[<>=!]/.test(text))
            return text;
        const items = this.itemsOf(c);
        const q = text.toLowerCase();
        const hit = items.filter(i => i.label.toLowerCase().includes(q) || i.value.toLowerCase() === q);
        // 체크 콤보: 값이 '코드,코드' 라 목록(in) 대신 포함 — 명칭 하나가 맞으면 그 코드로
        if (c.def.editor === 'checkCombo') {
            const ex = hit.find(i => i.value.toLowerCase() === q);
            return ex ? ex.value : hit.length === 1 ? hit[0].value : text;
        }
        return hit.length ? hit.map(i => i.value).join(',') + (hit.length === 1 ? ',' : '') : text;
    }
    /** @internal */ itemsOf(c, row) {
        const ri = this.ruleOf(c, row)?.items;
        if (ri)
            return ri;
        const it = c.def.items;
        return typeof it === 'function' ? it() : it ?? [];
    }
    /** @internal */ comboMap(c, row) {
        const items = this.itemsOf(c, row);
        let m = this.comboMaps.get(items);
        if (!m) {
            m = new Map(items.map(i => [i.value, i]));
            this.comboMaps.set(items, m);
        }
        return m;
    }
    /** @internal */ isChecked(c, v) { const on = this.checkValues(c)[0]; return v === on || v === true; }
    /** @internal */ checkValues3(c) {
        const [on, off] = this.checkValues(c), m = c.def.checkValues?.[2];
        return [on, off, m !== undefined ? m : on === true ? null : '?'];
    }
    /** @internal */ checkValues(c) {
        if (c.def.checkValues)
            return [c.def.checkValues[0], c.def.checkValues[1]];
        return this.ds.cols[c.id]?.type === 'boolean' ? [true, false] : ['Y', 'N'];
    }
    /** @internal */ ruleOf(c, row) {
        if (row == null || row < 0 || !c.def.editRule)
            return null;
        return c.def.editRule(row, this.rowEdit && this.rowEdit.row === row ? this.rowEdit.proxy : this.ds) || null;
    }
    /** @internal */ editorOf(c, row) {
        const d = c.def;
        if (c.special || (d.value && !d.valueSetter))
            return null;
        const rule = this.ruleOf(c, row);
        if (rule && rule.editor !== undefined)
            return rule.editor || null;
        if (d.editor === false)
            return null;
        if (d.editor)
            return d.editor;
        if (!this.opts.editable)
            return d.items ? 'combo' : null;
        if (d.items)
            return 'combo';
        const col = this.ds.cols[c.id];
        if (col?.type === 'boolean')
            return 'check';
        if (col?.kind === 'num')
            return 'number';
        if (col?.type === 'date' || d.format === 'date')
            return 'date';
        return 'text';
    }
    /** @internal */ canEditCol(c, row) {
        if (!this.opts.editable || row < 0)
            return false;
        if (this.ds.state[row] === RowState.DELETE)
            return false; // 삭제 행(showDeletedRows 로 보일 때)은 편집 안 함
        if (!this.editorOf(c, row))
            return false;
        if (this.protectOn && this.isCellLocked(row, c.id))
            return false;
        if (c.def.access && this.columnAccess(c.def) !== 'edit')
            return false;
        if (this.ruleOf(c, row)?.readOnly)
            return false;
        const ro = c.def.readOnly;
        if (ro === true || (typeof ro === 'function' && ro(row, this.ds)))
            return false;
        return this.opts.onBeforeEdit?.({ row, col: c.id, grid: this }) !== false;
    }
    on(type, fn) {
        const set = type === 'beforeRowFocus' ? this.focusGuards : type === 'rowFocus' ? this.focusListeners : type === 'cellFocus' ? this.cellFocusListeners : type === 'beforeChange' ? this.beforeChangeListeners : type === 'beforeRowInsert' ? this.beforeInsertListeners : type === 'beforeRowDelete' ? this.beforeDeleteListeners : type === 'beforeCellFocus' ? this.beforeCellFocusListeners : this.selListeners;
        set.add(fn);
        if (type === 'cellFocus' || type === 'selectionChange')
            this.queueFocusEvents();
        return () => { set.delete(fn); };
    }
    /** 행 표시기 기호 (우선순위: 편집 중 > 검증 중 > 오류 > 포커스 > 추가·수정) */
    /** 셀 검증 수준 → aria-invalid(오류·경고) · aria-description("오류: 문구") — 수준이 있는 칸만 (드묾) */
    /** @internal */ ariaLevel(ce, lvl, row, col) {
        const el = ce.el;
        if (ce.alv !== lvl) {
            ce.alv = lvl;
            if (lvl === 'error' || lvl === 'warning')
                el.setAttribute('aria-invalid', 'true');
            else
                el.removeAttribute('aria-invalid');
        }
        const d = lvl ? `${this.t(lvl === 'error' ? '오류' : lvl === 'warning' ? '경고' : '안내')}: ${this.getCellMessage(row, col)?.message ?? ''}` : '';
        if (ce.ades !== d) {
            ce.ades = d;
            if (d)
                el.setAttribute('aria-description', d);
            else
                el.removeAttribute('aria-description');
        }
    }
    /** @internal */ indicatorOf(row, vi) {
        const st = this.ds.rowState(row);
        const sc = st === RowState.INSERT ? ' og-ind-i' : st === RowState.UPDATE ? ' og-ind-u' : '';
        const stt = st === RowState.INSERT ? ' · 추가' : st === RowState.UPDATE ? ' · 수정' : '';
        if (st === RowState.DELETE)
            return { sym: 'D', cls: 'og-ind-d', title: this.t('삭제') };
        if ((this.editing && this.editing.row === row) || (this.rowEdit && this.rowEdit.row === row))
            return { sym: '✎', cls: 'og-ind-ed' + sc, title: this.t('편집 중') + stt };
        if (this.asyncPend.size && this.isValidating(row))
            return { sym: '⟳', cls: 'og-ind-pend' + sc, title: this.t('확인 중') + stt };
        if (this.rowHasError(row))
            return { sym: this.opts.cellStateIcons === false ? '⚠' : '✕', cls: 'og-ind-err' + sc, title: this.t('오류') + stt }; // 1.2.0: 오류 ✕ · 경고 ⚠ (색만으로 구분하지 않음)
        if ((this.warns.size || this.asyncWarn.size || this.rowWarns.size) && this.rowHasWarn(row))
            return { sym: '⚠', cls: 'og-ind-warn' + sc, title: this.t('경고') + stt };
        if (vi === this.focus.vi)
            return { sym: '▶', cls: 'og-ind-cur' + sc, title: this.t('현재 행') + stt };
        return { sym: st === RowState.INSERT ? '+' : st === RowState.UPDATE ? '*' : '', cls: sc.trim(), title: stt.slice(3) };
    }
    /** @internal */ rowHasError(row) {
        if (!this.errors.size && !this.asyncErr.size && !this.rowErrs.size)
            return false;
        const p = row + ':';
        for (const m of [this.errors, this.asyncErr, this.rowErrs])
            for (const k of m.keys())
                if (k.startsWith(p))
                    return true;
        return false;
    }
    // ═══════════════════════ 마우스 ═══════════════════════
    /** @internal */ hit(e) {
        const t = e.target;
        const rowEl = t.closest('.og-r');
        if (!rowEl || rowEl.__slot == null)
            return null;
        const vi = rowEl.__pinVi ?? this.first + rowEl.__slot;
        const cellEl = t.closest('.og-c');
        if (cellEl && cellEl.__ci != null)
            return { vi, ci: cellEl.__ci };
        return { vi, ci: this.focus.ci };
    }
    /** @internal */ unmerge(ce) { ce.m0 = ce.m1 = undefined; ce.el.style.paddingTop = ce.el.style.paddingBottom = ''; this.mergedEls.delete(ce); }
    /** 병합 셀이 위·아래로 잘리면 글자를 보이는 부분 가운데로 (패딩으로 내용 영역을 줄임) */
    /** @internal */ centerMerged(vTop, vh) {
        const rh = this.rh;
        for (const ce of this.mergedEls) {
            if (ce.el.hidden || ce.m0 == null || !ce.el.isConnected || !this.layers.includes(ce.el.parentElement?.parentElement)) {
                this.unmerge(ce);
                continue;
            }
            const top = this.rowTop(ce.m0) - vTop, bot = this.rowTop(ce.m1 + 1) - vTop;
            const pt = Math.max(0, Math.min(-top, bot - top - rh)), pb = Math.max(0, Math.min(bot - vh, bot - top - rh - pt));
            const a = pt ? pt + 'px' : '', b = pb ? pb + 'px' : '';
            if (ce.el.style.paddingTop !== a)
                ce.el.style.paddingTop = a;
            if (ce.el.style.paddingBottom !== b)
                ce.el.style.paddingBottom = b;
        }
    }
    // ── 채우기 핸들 ──
    /** @internal */ fillOn() { return !!this.opts.fillHandle && !!this.opts.editable && !this.editing; }
    /** @internal */ placeFillHandle(vTop, sl) {
        const fh = this.fh, { r1, c1 } = this.range(), c = this.cols[c1], main = this.bodyPanes[1];
        let x = -1, y = -1;
        if (this.fillOn() && c && !c.special && this.disp[r1] >= 0 && this.canEditCol(c, this.disp[r1])) {
            x = c.pane === 0 ? c.left + c.width : c.pane === 2 ? main.parentElement.clientWidth - this.paneWidth[2] + c.left + c.width : this.paneWidth[0] + c.left + c.width - sl;
            y = (c.lh ? this.rowTop(r1) + (c.lt + c.lh) * this.lineH : this.rowTop(r1 + 1)) - vTop; // 멀티로우: 셀 아래 끝
            if (c.pane === 1 && (x < this.paneWidth[0] || x > this.paneWidth[0] + main.clientWidth + 1))
                x = -1;
            if (y < 0 || y > main.clientHeight + 1)
                x = -1;
        }
        if (x < 0) {
            if (!fh.hidden)
                fh.hidden = true;
            return;
        }
        fh.hidden = false;
        fh.style.left = x - 4 + 'px';
        fh.style.top = y - 4 + 'px';
    }
}
/**
 * 1.2.0: 앱 전체 기본 옵션 — 그리드를 만들기 전에 한 번 (예: `OneGrid.defaults.zIndexBase = 1100`, `OneGrid.defaults.theme = 'dark'`, `OneGrid.defaults.rowHeight = 28`).
 * 우선순위: 직접 준 옵션 > 디자인 유형(preset) > defaults > 내장 기본값. 이미 만든 그리드에는 적용 안 됨
 */
OneGrid.defaults = {};
applyMixins(OneGrid, [GridFilter, GridFind, GridLayoutState, GridGroup, GridHeader, GridSelect, GridRows, GridUndo, GridExport, GridNewRow, GridEditForm, GridEdit, GridDropdown, GridKeys, GridMouse, GridMenu, GridClipboard, GridRowDrag, GridInput, GridRowEdit, GridCheckCombo]);
